import {
	AUCTION_BID_KIND,
	AUCTION_KIND,
	AUCTION_SETTLEMENT_KIND,
	AUCTION_SETTLEMENT_POLICY,
	getAuctionTagValue,
} from '@/lib/auctionSettlement'
import { AUCTION_MIN_DURATION_SECONDS, validateAuctionPublishInput } from '@/lib/auctionPublishValidation'
import { ORDER_MESSAGE_TYPE, ORDER_PROCESS_KIND } from '@/lib/schemas/order'
import { configStore } from '@/lib/stores/config'
import { nip60Actions, type AuctionP2pkKeyScheme, AuctionBidLockMutationPossibleError } from '@/lib/stores/nip60'
import type { ProductShippingSelectionInput } from '@/lib/utils/productShippingSelections'
import { getBidAmount, getBidStatus, markAuctionAsDeleted } from '@/queries/auctions'
import { isStructurallyValidSettledSettlement } from '@/lib/auction/events'
import { toRawEvent, type NostrEventLike } from '@/lib/nostr/eventLike'
import { evaluateReserveNotMetGuard, resolveReserveNotMetPublish } from '@/lib/auction/reserveGuard'
import { generateAuctionDerivationPath } from '@/lib/auctionPathOracle'
import { deriveAuctionChildP2pkPubkeyFromXpub } from '@/lib/auctionP2pk'
import { hashToCurveHexFromString } from '@/lib/cashu/hashToCurve'
import { buildDleqProofs, fetchDleqKeysetsForBidsDetailed } from '@/lib/cashu/dleq'
import { buildBidEventTags, buildPathReleaseTags } from '@/lib/auction/tagBuilders'
import {
	buildAuctionClaimPublicMarkerTags,
	createPrivateAuctionClaimMessageForActiveSigner,
} from '@/lib/auctions/privateAuctionClaimMessage'
import {
	findLatestBidderRecordForAuction,
	updateBidderRecordStatus,
	upsertBidderRecord,
	walkBidderRecordChain,
	persistPreLockRecoveryRecord,
	removePreLockRecoveryRecord,
	type AuctionBidPreLockRecoveryRecord,
} from '@/lib/auction/bidderRecords'
import { loadUserData, saveUserData } from '@/lib/wallet/storage'
import {
	AUCTION_MIN_BID_LEG_SATS,
	AUCTION_MIN_BID_SATS,
	AUCTION_PATH_RELEASE_KIND,
	type PathReleaseReason,
	type Nut7ProofState,
} from '@/lib/auction/constants'
import { assertAuctionMintsSupportDleq } from '@/lib/auction/validation'
import { preflightAuctionSettlementP2pkChain } from '@/lib/auctionSettlementP2pk'
import { getEncodedToken, getDecodedToken, type MintKeyset, type Proof } from '@cashu/cashu-ts'
import { getPublicKey } from '@noble/secp256k1'
import { auctionKeys, orderKeys } from '@/queries/queryKeyFactory'
import { getUser, publish as publishNostrEvent, sign as signNostrEvent } from '@/lib/nostr/io'
import type { EventTemplate, NostrEvent, PublishOptions } from '@/lib/nostr/io'
import { getEventHash } from 'nostr-tools'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRef } from 'react'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'

export interface AuctionSpecEntry {
	key: string
	value: string
}

/**
 * Settlement grace presets (AUCTIONS.md §4.1). Seconds between
 * `max_end_at` and the bid's Cashu locktime — i.e. how long the seller
 * has to publish the kind-1024 settlement after bidding closes.
 */
export const AUCTION_SETTLEMENT_GRACE_PRESETS = {
	'5min': 300,
	'1h': 3600,
	'3h': 10800,
} as const
export type AuctionSettlementGracePreset = keyof typeof AUCTION_SETTLEMENT_GRACE_PRESETS

/**
 * Anti-snipe curve shapes (AUCTIONS.md §6.1). Controls how the bid
 * floor scales in `(end_at, max_end_at]`.
 */
export type AuctionMinBidCurveShape = 'none' | 'linear' | 'exponential'

/**
 * Peak-multiplier presets for the anti-snipe curve. Applied as
 * `floor = baseline × peak` at `t = max_end_at`. 1.0 is implicit when
 * `shape = 'none'`.
 */
export const AUCTION_MIN_BID_CURVE_PEAK_PRESETS = [2, 5, 10] as const
export type AuctionMinBidCurvePeakPreset = (typeof AUCTION_MIN_BID_CURVE_PEAK_PRESETS)[number]

/**
 * Anti-snipe window presets — minutes added to `end_at` to compute
 * `max_end_at`. Drives the duration of the curve. `0` disables the
 * window entirely (max_end_at = end_at, no curve regardless of shape).
 */
export const AUCTION_ANTI_SNIPE_WINDOW_PRESETS_MINUTES = [0, 5, 15, 30] as const
export type AuctionAntiSnipeWindowMinutesPreset = (typeof AUCTION_ANTI_SNIPE_WINDOW_PRESETS_MINUTES)[number]

export interface AuctionFormData {
	title: string
	summary: string
	description: string
	startingBid: string
	bidIncrement: string
	reserve?: string
	startAt?: string
	endAt: string
	/**
	 * Seconds added to `end_at` to compute `max_end_at`. Zero = no
	 * anti-snipe window. When zero the curve has zero duration and is
	 * effectively disabled regardless of `minBidCurveShape`.
	 */
	antiSnipeWindowMinutes: AuctionAntiSnipeWindowMinutesPreset
	/** Shape of the anti-snipe floor curve in the `(end_at, max_end_at]` window. */
	minBidCurveShape: AuctionMinBidCurveShape
	/** Floor multiplier applied at `t = max_end_at`. Only relevant when shape ≠ none. */
	minBidCurvePeakMultiplier: AuctionMinBidCurvePeakPreset
	/**
	 * Settlement grace preset — chosen as `5min`/`1h`/`3h` in the UI,
	 * mapped to seconds at publish time via
	 * `AUCTION_SETTLEMENT_GRACE_PRESETS[preset]`.
	 */
	settlementGracePreset: AuctionSettlementGracePreset
	mainCategory: string
	categories: string[]
	imageUrls: string[]
	specs: AuctionSpecEntry[]
	shippings: ProductShippingSelectionInput[]
	trustedMints: string[]
	isNSFW: boolean
	enableLiveChat: boolean
	/**
	 * Pubkey of the validator (auditor) the seller wants listed on the
	 * auction's `auditors` tag. Empty string = "use the app's configured
	 * default" (resolved at publish time via `getAuctionAuditorsOrThrow`).
	 *
	 * Single value today; the kind-30408 event supports a multi-auditor
	 * list. The Phase 7 reputation UI is expected to grow this into a
	 * multi-select; for the demo a single pubkey is fine.
	 */
	auditorPubkey: string
}

export interface AuctionBidFormData {
	auctionEventId: string
	auctionCoordinates: string
	amount: number
	/**
	 * Auction `start_at` (unix seconds). Required so the publish path can
	 * refuse bids placed before the auction has officially opened. Without
	 * this gate, bids land on the relay with `created_at < start_at` and are
	 * silently rejected by the settlement filter — the seller and bidder both
	 * see "0 bids / starting price" while the events sit on the relay.
	 */
	auctionStartAt: number
	auctionEffectiveEndAt: number
	auctionLocktimeAt: number
	/**
	 * Per-auction settlement grace in seconds (the gap between max_end_at and
	 * the Cashu locktime). Read from the auction event's `settlement_grace`
	 * tag — see AUCTIONS.md §4.1. Authoritative at bid time; the locktime
	 * stamped into the proof is `auctionLocktimeAt + settlementGraceSeconds`.
	 */
	settlementGraceSeconds: number
	sellerPubkey: string
	p2pkXpub: string
	/**
	 * Ordered list of the auction's trusted mints (`mint` tags on
	 * kind 30408). The lock flow walks this list and picks the first
	 * one where the bidder's NIP-60 wallet has enough balance for the
	 * delta amount. Falls back to `DEFAULT_BID_MINT` only when the list
	 * is empty (legacy bid forms that didn't pass mints).
	 *
	 * Replaces the older `mint?: string` field which always picked
	 * the seller's first declared mint regardless of whether the
	 * bidder actually had any sats there.
	 */
	mintCandidates: string[]
}

// `AuctionPathGrantResponse`, `openAuctionPathOracleClient`,
// `requestAuctionPathGrant` — all removed. They belonged to the v1
// path-oracle scheme where a coordinator handed out paths over CVM.
// Under `cashu_p2pk_bidder_path_v1` the bidder generates the path
// locally; there's nothing to "request" from anyone. Phase 3 wires the
// new bid-creation flow into `publishAuctionBid` below.

export interface AuctionSettlementFormData {
	auctionEventId: string
	auctionCoordinates?: string
	/**
	 * Optional expected outcome. When omitted the backend computes it from the
	 * bids + reserve and the client publishes whatever it resolves to. Provide
	 * this only if the caller wants a safety assertion that their expectation
	 * matches reality — mismatch causes the backend to reject.
	 */
	status?: 'settled' | 'reserve_not_met'
	winningBidEventId?: string
	reason?: string
	/**
	 * ADR-0011 review R3 — seller escape hatch. When `reserve_not_met` is
	 * blocked ONLY because a reserve-meeting bid's DLEQ keyset evidence is
	 * unavailable (a transient mint outage, or a crafted bid naming a keyset the
	 * mint does not advertise), the seller may set this to publish the terminal
	 * state anyway. The unavailability is recorded in the settlement's `reason`
	 * tag so the override is auditable on the relay. It never overrides the
	 * `blocked` case (a validated reserve-meeting winner already exists).
	 */
	dleqEvidenceOverride?: boolean
}

const HEX_PUBKEY_RE = /^[0-9a-f]{64}$/i

/**
 * Resolve the auditor pubkey(s) to bake into a new auction's
 * `auditors` tag(s). Selection priority:
 *
 *   1. The seller's explicit choice from the form (`formData.auditorPubkey`).
 *      Validated as 32-byte hex.
 *   2. The app's configured default (`configStore.config.cvmServerPubkey`).
 *
 * Throws when neither is available — without an auditor the auction
 * has no validator emitting kind-30440 verdicts, which means clients
 * have nothing to filter or aggregate against.
 */
const getAuctionAuditorsOrThrow = (formAuditorPubkey?: string): string[] => {
	// Single auditor today; kind-30408 supports a list (multiple
	// `auditors` tags). Phase 7 (reputation UI) is expected to grow this
	// into a multi-select. Falls back to the app's configured validator
	// pubkey when the form field is empty, so dev/seed flows that don't
	// choose an auditor explicitly still get one.
	const explicit = formAuditorPubkey?.trim()
	if (explicit) {
		if (!HEX_PUBKEY_RE.test(explicit)) {
			throw new Error('Selected auditor pubkey is not a 32-byte hex Nostr pubkey.')
		}
		return [explicit]
	}
	const fallback = configStore.state.config.cvmServerPubkey?.trim()
	if (!fallback) {
		throw new Error('No auditor pubkey selected and no default auditor available. Wait for app config to load and try again.')
	}
	return [fallback]
}

/**
 * Wrap an error thrown from a specific step of the bid flow with a tag that
 * names the step. Makes the eventual toast unambiguous about whether the
 * rate limit / failure came from the mint, the issuer, or the relay.
 */
const tagBidError = (step: string, cause: unknown): Error => {
	const detail = cause instanceof Error ? cause.message : String(cause)
	const tagged = new Error(`[${step}] ${detail}`)
	if (cause instanceof Error && cause.stack) {
		tagged.stack = cause.stack
	}
	return tagged
}

export const createAuctionEvent = async (formData: AuctionFormData, auctionId?: string): Promise<EventTemplate> => {
	const validated = validateAuctionPublishInput(formData, { minDurationSeconds: AUCTION_MIN_DURATION_SECONDS })

	const id = auctionId || `auction_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`
	const startingBid = String(validated.startingBid)
	const bidIncrement = String(validated.bidIncrement)
	// Defensive `?? 0` even though `validated.reserve` is now non-optional.
	// `String(undefined)` produced `"undefined"` on the wire for auction
	// `1618640c…0881` on staging (kind-30408 tag). Belt-and-braces.
	const reserve = String(validated.reserve ?? 0)
	// AUCTIONS.md §6.0 — the three timestamps:
	//   end_at      → nominal close. Floor stays flat in [start_at, end_at].
	//   max_end_at  → hard bidding cutoff. Equals end_at + window_seconds
	//                 where window_seconds is the seller-chosen anti-snipe
	//                 window (or 0 → max_end_at = end_at, no curve).
	//   locktime    → max_end_at + settlement_grace, mint-enforced refund opens.
	//
	// `min_bid_curve` defines the floor ramp in `(end_at, max_end_at]`.
	// AUCTIONS.md §6.1 — replaces the legacy `extension_rule:anti_sniping:*`
	// scheme. Floor is monotonic over time; bidder UI displays the floor
	// at `client_now`, server enforces with a 5 s grace.
	const minBidCurveTagValue =
		formData.minBidCurveShape === 'none' ? 'none:1.0' : `${formData.minBidCurveShape}:${formData.minBidCurvePeakMultiplier}.0`
	const settlementGraceSeconds = AUCTION_SETTLEMENT_GRACE_PRESETS[formData.settlementGracePreset]
	const keyScheme: AuctionP2pkKeyScheme = 'hd_p2pk'
	// Validators auctions trust. One pubkey per `auditors` tag; the form
	// gives the seller a single field today and falls back to the app's
	// configured default. Phase 7 (reputation UI) will grow this into a
	// multi-select.
	const auditorsList = getAuctionAuditorsOrThrow(formData.auditorPubkey)
	const p2pkXpub = await nip60Actions.getAuctionP2pkXpub()

	const imageTags: string[][] = validated.imageUrls.map((url, index) => ['image', url, '800x600', String(index)])
	const categoryTags: string[][] = []
	if (formData.mainCategory) {
		categoryTags.push(['t', formData.mainCategory])
	}
	for (const category of formData.categories) {
		if (category && category.trim()) {
			categoryTags.push(['t', category.trim()])
		}
	}

	const specTags: string[][] = (formData.specs ?? [])
		.filter((spec) => spec && spec.key.trim() && spec.value.trim())
		.map((spec) => ['spec', spec.key.trim(), spec.value.trim()])

	const shippingTags: string[][] = validated.shippings.map((ship) =>
		ship.extraCost ? ['shipping_option', ship.shippingRef, ship.extraCost] : ['shipping_option', ship.shippingRef],
	)

	const tags: string[][] = [
		['d', id],
		['title', validated.title],
		...(validated.summary ? [['summary', validated.summary]] : []),
		['auction_type', 'english'],
		['start_at', String(validated.startAt)],
		['end_at', String(validated.endAt)],
		['currency', 'SAT'],
		['price', startingBid, 'SAT'],
		['starting_bid', startingBid, 'SAT'],
		['bid_increment', bidIncrement],
		['reserve', reserve],
		...validated.trustedMints.map((mint) => ['mint', mint]),
		// Bidder-held-path scheme: list one or more validator pubkeys whose
		// kind-30440 verdicts compliant clients consult to gate bid validity
		// for THIS auction. See AUCTIONS.md §4.1.
		...auditorsList.map((auditor) => ['auditors', auditor]),
		// Explicit values for the per-auction validator parameters.
		// Defaults match `DEFAULT_AUDITOR_QUORUM` / `DEFAULT_MAX_SKEW_SECONDS`
		// in src/lib/auction/constants.ts — emitting them explicitly
		// makes the auction round-trip cleanly through compliant
		// validators that strictly check tag presence.
		['auditor_quorum', String(auditorsList.length)],
		['max_skew_sec', '120'],
		['max_end_at', String(validated.maxEndAt)],
		['settlement_grace', String(settlementGraceSeconds)],
		['min_bid_curve', minBidCurveTagValue],
		['key_scheme', keyScheme],
		['p2pk_xpub', p2pkXpub],
		['settlement_policy', AUCTION_SETTLEMENT_POLICY],
		['schema', 'auction_v1'],
		...imageTags,
		...categoryTags,
		...specTags,
		...shippingTags,
		...(formData.isNSFW ? [['content-warning', 'nsfw']] : []),
		...(formData.enableLiveChat ? [['live_chat', 'enabled']] : []),
	]

	return {
		kind: 30408,
		content: validated.description,
		tags,
		created_at: Math.floor(Date.now() / 1000),
	}
}

export const publishAuction = async (formData: AuctionFormData, auctionId?: string): Promise<string> => {
	const validated = validateAuctionPublishInput(formData, { minDurationSeconds: AUCTION_MIN_DURATION_SECONDS })

	// ADR-0011 Decision 6 — reject post-rollout auctions whose allowlisted
	// mints do not advertise NUT-12 DLEQ support (Decision 4). Runs before any
	// event is built or signed so a non-DLEQ auction is never published.
	await assertAuctionMintsSupportDleq(validated.trustedMints)

	const template = await createAuctionEvent(formData, auctionId)
	const event = await signNostrEvent(template)
	await publishRequired(event)
	return event.id
}

export const usePublishAuctionMutation = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: async (formData: AuctionFormData) => publishAuction(formData),
		onSuccess: async () => {
			const user = await getUser()
			const userPubkey = user?.pubkey || ''
			await queryClient.invalidateQueries({ queryKey: auctionKeys.all })
			if (userPubkey) {
				await queryClient.invalidateQueries({ queryKey: auctionKeys.byPubkey(userPubkey) })
			}
			toast.success('Auction published successfully')
		},
		onError: (error) => {
			console.error('Failed to publish auction:', error)
			toast.error(`Failed to publish auction: ${error instanceof Error ? error.message : String(error)}`)
		},
	})
}

export const deleteAuction = async (auctionDTag: string): Promise<boolean> => {
	const user = await getUser()
	if (!user?.pubkey) throw new Error('No active user')

	const template: EventTemplate = {
		kind: 5,
		content: 'Auction deleted',
		tags: [['a', `30408:${user.pubkey}:${auctionDTag}`]],
		created_at: Math.floor(Date.now() / 1000),
	}

	const event = await signNostrEvent(template)
	await publishRequired(event)
	return true
}

export const useDeleteAuctionMutation = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: async (auctionDTag: string) => deleteAuction(auctionDTag),
		onSuccess: async (_success, auctionDTag) => {
			markAuctionAsDeleted(auctionDTag)

			const user = await getUser()
			const userPubkey = user?.pubkey || ''

			await queryClient.invalidateQueries({ queryKey: auctionKeys.all })
			if (userPubkey) {
				await queryClient.invalidateQueries({ queryKey: auctionKeys.byPubkey(userPubkey) })
			}
			toast.success('Auction deleted successfully')
		},
		onError: (error) => {
			console.error('Failed to delete auction:', error)
			toast.error(`Failed to delete auction: ${error instanceof Error ? error.message : String(error)}`)
		},
	})
}

const DEFAULT_BID_MINT = 'https://nofees.testnut.cashu.space'

const ACTIVE_BID_STATUSES = new Set(['locked', 'accepted', 'active', 'unknown'])

const getFirstTagValue = getAuctionTagValue

const isSpentTokenError = (error: unknown): boolean => {
	const message = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase()
	return message.includes('already spent') || message.includes('token spent') || message.includes('proof not found')
}

const resolveLatestActiveBidByBidder = (bids: NostrEventLike[], bidderPubkey: string): NostrEventLike | null => {
	const bidderBids = bids.filter((bid) => bid.pubkey === bidderPubkey && ACTIVE_BID_STATUSES.has(getBidStatus(bid)))
	if (!bidderBids.length) return null

	return bidderBids.sort((a, b) => {
		const amountDelta = getBidAmount(b) - getBidAmount(a)
		if (amountDelta !== 0) return amountDelta
		const createdAtDelta = (b.created_at || 0) - (a.created_at || 0)
		if (createdAtDelta !== 0) return createdAtDelta
		return b.id.localeCompare(a.id)
	})[0]
}

/**
 * Publish a bidder-held-path bid (kind 1023) — AUCTIONS.md §4.2.
 *
 * Flow:
 *   1. Sanity-check the form + window (the bidder shouldn't sign a bid
 *      outside the auction window even though the validator would catch it).
 *   2. Generate a fresh high-entropy derivation path locally (§5.5).
 *   3. Compute `seller_child = derive(p2pk_xpub, path)`.
 *   4. Generate a fresh refund keypair (per-bid for privacy + isolation).
 *   5. Lock the Cashu bid via the NIP-60 wallet (1-of-1 P2PK to seller_child
 *      with refund timelock).
 *   6. Decode the locked token to extract each proof's `secret` (= lock_secret
 *      for the bid event) and compute `proof_y = hash_to_curve(secret)`.
 *   7. Publish kind-1023 with all (lock_secret, proof_y) pairs embedded.
 *   8. Persist a {path, refundPrivKey, fullProofs} record locally so we can
 *      (a) release the path at settlement, (b) refund via timelock if we
 *      grief or the seller never settles.
 *
 * Returns the published bid event id.
 */
export const publishAuctionBid = async (formData: AuctionBidFormData): Promise<string> => {
	if (!formData.auctionEventId) throw new Error('Auction event id is required')
	if (!formData.auctionCoordinates) throw new Error('Auction coordinates are required')
	if (!formData.sellerPubkey) throw new Error('Seller pubkey is required')
	if (!formData.p2pkXpub) throw new Error('Auction p2pk_xpub is required for path derivation')
	if (!Number.isSafeInteger(formData.amount) || formData.amount < AUCTION_MIN_BID_SATS) {
		throw new Error(`Bid amount must be an integer of at least ${AUCTION_MIN_BID_SATS} sats`)
	}
	if (!Number.isFinite(formData.auctionStartAt) || formData.auctionStartAt <= 0) {
		throw new Error('Auction start time is required for bidding')
	}
	if (!Number.isFinite(formData.auctionEffectiveEndAt) || formData.auctionEffectiveEndAt <= 0) {
		throw new Error('Auction effective end time is required for bidding')
	}
	if (!Number.isFinite(formData.auctionLocktimeAt) || formData.auctionLocktimeAt <= 0) {
		throw new Error('Auction locktime base is required')
	}
	if (!Number.isFinite(formData.settlementGraceSeconds) || formData.settlementGraceSeconds <= 0) {
		throw new Error('Auction is missing settlement_grace — refusing to lock without an authoritative grace period')
	}

	const now = Math.floor(Date.now() / 1000)
	if (now < formData.auctionStartAt) throw new Error('Auction has not started yet')
	if (now >= formData.auctionEffectiveEndAt) throw new Error('Auction already ended')
	if (now >= formData.auctionLocktimeAt) throw new Error('Auction has reached its hard bidding cutoff')

	const bidderUser = await getUser()
	if (!bidderUser?.pubkey) throw new Error('No active user')
	const bidderPubkey = bidderUser.pubkey

	// Step 1.5 — rebid detection. If this bidder already has a leg on
	// this auction, the new bid is a chain rebid: we lock ONLY the
	// delta `formData.amount - prev_leg.amount`, point at the prev leg
	// via `prev_bid`, and the seller will walk the chain on settle.
	// This keeps the bidder's total collateral equal to their latest
	// committed bid amount rather than the sum-of-all-rebids.
	//
	// AUCTIONS.md §4.2 — `prev_bid: previous bid event id from same
	// bidder (replacement chain)`. AUCTIONS.md §8 — seller "derives
	// every child privkey in the winner's chain, swaps each leg at the
	// mint" with a uniform locktime across legs.
	const prevLeg = findLatestBidderRecordForAuction(formData.auctionEventId)
	const prevLegAmount = prevLeg?.amount ?? 0
	if (prevLeg && formData.amount <= prevLeg.amount) {
		throw new Error(`Rebid (${formData.amount} sats) must exceed your previous bid on this auction (${prevLeg.amount} sats)`)
	}
	const legLockAmount = formData.amount - prevLegAmount
	if (!Number.isSafeInteger(legLockAmount) || legLockAmount < AUCTION_MIN_BID_LEG_SATS) {
		throw new Error(`Bid raise must be an integer of at least ${AUCTION_MIN_BID_LEG_SATS} sats`)
	}

	// Step 2/3 — generate path + derive child pubkey locally. Fresh per
	// leg: each rebid gets its own path/child so refund branches don't
	// cluster across legs.
	const derivationPath = generateAuctionDerivationPath()
	const childPubkey = deriveAuctionChildP2pkPubkeyFromXpub(formData.p2pkXpub, derivationPath)

	// Step 4 — fresh per-leg refund keypair. Privacy: refund branches
	// don't cluster. Isolation: a leaked refund key only affects this
	// one leg.
	const refundPrivateKeyBytes = crypto.getRandomValues(new Uint8Array(32))
	const refundPubkeyBytes = getPublicKey(refundPrivateKeyBytes, true)
	const refundPubkey = bytesToLowerHex(refundPubkeyBytes)
	const refundPrivateKey = bytesToLowerHex(refundPrivateKeyBytes)

	// Step 5 — lock at the mint. We lock the DELTA (`legLockAmount`),
	// not the full cumulative bid. The previous leg(s) stay locked at
	// their own pubkeys/locktime until settled or refunded — the chain
	// settles together, the delta is just this leg's contribution.
	//
	// Locktime invariant (AUCTIONS.md §6.0 / §8.1 line 1090): every
	// leg in a chain shares the same locktime — `max_end_at +
	// settlement_grace`. We compute it from the auction (not from the
	// previous leg) because both legs reference the same auction event.
	const locktime = formData.auctionLocktimeAt + formData.settlementGraceSeconds
	if (prevLeg && prevLeg.locktime !== locktime) {
		// Should be impossible (same auction → same max_end_at +
		// settlement_grace), but if the seller mutated the auction
		// event in between, locktimes can drift. Refuse rather than
		// emit a chain with non-uniform locktimes.
		throw new Error(
			`Locktime invariant broken: previous leg locktime=${prevLeg.locktime}, new leg locktime=${locktime}. Auction's max_end_at or settlement_grace was changed since the previous bid.`,
		)
	}
	const mintCandidates = formData.mintCandidates?.length ? formData.mintCandidates : []

	// #1235 round-3 B1 — pre-lock recovery record (fail-closed confirmed write).
	// From the moment the lock call below may swap/lock at the mint, failure
	// handling must assume the mint mutated state. The ONLY durable copy of
	// this leg's refund private key must already be on disk BEFORE that call —
	// otherwise a post-swap throw loses the refund authority and the leg is
	// not even timelock-reclaimable. `persistPreLockRecoveryRecord` uses
	// CONFIRMED-WRITE semantics (strict save + read-back equality): if the
	// record is not durably present, we must NOT proceed to the mint.
	const preLockRecoveryRecordId = uuidv4()
	const preLockRecoveryRecord: AuctionBidPreLockRecoveryRecord = {
		id: preLockRecoveryRecordId,
		createdAt: Date.now(),
		auctionEventId: formData.auctionEventId,
		auctionCoordinates: formData.auctionCoordinates,
		sellerPubkey: formData.sellerPubkey,
		p2pkXpub: formData.p2pkXpub,
		derivationPath,
		childPubkey,
		refundPubkey,
		refundPrivateKey,
		// Best-effort pre-lock diagnostic: the authoritative mint is selected
		// inside lockAuctionBidFunds and is recorded on the wallet's pending
		// token + the bidder record once the lock returns.
		mintUrl: mintCandidates[0] ?? '',
		legLockAmount,
		cumulativeAmount: formData.amount,
		locktime,
		prevBidEventId: prevLeg?.bidEventId ?? null,
	}
	try {
		persistPreLockRecoveryRecord(preLockRecoveryRecord)
	} catch (error) {
		// Fail closed BEFORE any mint interaction: nothing was locked, nothing
		// was mutated. The funding lifecycle's existing bare-error path handles
		// this class and its full re-submit fallback is provably safe here.
		throw new AuctionBidPreLockRecordWriteFailedError(refundPubkey, error)
	}

	// Step 5 — lock at the mint. Wrapped so the two post-lock realities are
	// distinguishable to every layer above:
	//   - AuctionBidLockMutationPossibleError (nip60, round-3 B1): a swap
	//     request may already have been sent — rethrow as
	//     AuctionBidLockOutcomeUncertainError carrying the pre-lock recovery
	//     record id. The PRE-LOCK RECORD SURVIVES (it is the refund
	//     authority for the uncertain leg).
	//   - any RAW error (nip60's pre-try validation: amount / wallet /
	//     balance / selection): provably nothing was mutated — remove the
	//     pre-lock record and rethrow raw; a full re-submit stays
	//     legitimate.
	let lockResult: Awaited<ReturnType<typeof nip60Actions.lockAuctionBidFunds>>
	try {
		lockResult = await nip60Actions.lockAuctionBidFunds({
			amount: legLockAmount,
			preferredMints: mintCandidates,
			locktime,
			refundPubkey,
			lockPubkey: childPubkey,
			auctionEventId: formData.auctionEventId,
			auctionCoordinates: formData.auctionCoordinates,
			sellerPubkey: formData.sellerPubkey,
			// Bidder-held-path scheme: no path issuer to record; supply the
			// path/child here so the wallet's pending-token diagnostics can
			// surface them for the bidder.
			derivationPath,
			childPubkey,
		})
	} catch (error) {
		if (error instanceof AuctionBidLockMutationPossibleError) {
			throw new AuctionBidLockOutcomeUncertainError({
				recoveryRecordId: preLockRecoveryRecordId,
				mintUrl: error.mintUrl,
				legAmount: error.amount,
				refundPubkey,
				cause: error,
				// #1235 round-3 fix 5: the reclaim promise is only honest when the
				// wallet durably observed the proofs — thread the flag through.
				pendingTokenPersisted: error.pendingTokenPersisted,
			})
		}
		removePreLockRecoveryRecord(refundPubkey)
		throw error
	}

	// #1235 follow-up (post-lock error model): from the moment the lock
	// above succeeds, the leg's sats are locked at the mint and every step
	// below can still fail. Two failure tiers with DIFFERENT recovery
	// semantics, which the funding lifecycle must be able to distinguish:
	//
	//   1. Sign/broadcast failure AFTER the durable recovery record + the
	//      rebroadcast cache were persisted (inner try below) →
	//      AuctionBidPublishFailedError(bidEvent.id): the leg is safely
	//      retryable by rebroadcasting the exact cached signed event.
	//
	//   2. ANY other post-lock failure (proof extraction, event
	//      finalization, the STRICT recovery-record write, cache write)
	//      → AuctionBidLockedButUnpublishedError(lockResult.tokenId):
	//      funds are locked but there is no durably-recoverable publishable
	//      kind-1023. Retrying MUST NOT fall back to the full pipeline —
	//      that would re-derive a fresh path and RE-LOCK the delta
	//      (double-lock). Recovery for this leg is RECLAIM-ONLY.
	let finalizedBidEventId: string | null = null
	try {
		// Step 6 — extract lock_secret + proof_y directly from the locked
		// proofs. We pull `proofs` off the lock result rather than
		// decoding the encoded `token` because token decode fails on v2
		// short keyset IDs without a mint keyset map — see
		// AUCTIONS.md §5 history and `LockAuctionBidFundsResult.proofs`.
		const proofs = lockResult.proofs
		if (!proofs.length) throw new Error('Lock result contained no proofs')
		const lockSecrets = proofs.map((proof: Proof) => proof.secret)
		const proofYs = proofs.map((proof: Proof) => hashToCurveHexFromString(proof.secret))

		// Stamp the bid at signing time, NOT at pre-flight. `now` above was
		// captured before `lockAuctionBidFunds` (a Cashu mint swap); reusing it
		// here would spend the bid's own `max_skew_sec` budget on the lock
		// latency. A lock+sign round trip slower than the configured skew makes
		// `created_at` stale relative to every validator's `observed_at`, so the
		// verdicts the bid needs for quorum become ineligible and the funded bid
		// can never be confirmed. Base filled `created_at` in at finalization
		// (i.e. post-lock); the seam preserves whatever the template carries, so
		// the timestamp must be taken here (review 2026-09-18, item 1).
		const publishedAt = Math.floor(Date.now() / 1000)

		// ADR-0011 Decision 1/8 — bids on DLEQ-REQUIRED auctions MUST publish
		// `dleq_proof` tags (one per locked proof, parallel to lock_secret/
		// proof_y). Build them from the locked proofs' DLEQ metadata;
		// `buildDleqProofs` is fail-closed and throws if any locked proof
		// lacks a DLEQ proof (or its blinding factor `r`). DLEQ is required
		// for every bid, so this always runs.
		const dleqProofs = buildDleqProofs(proofs)

		// Step 7 — publish kind-1023. `amount` is the cumulative bid value
		// (what the validator uses for the min-increment check); the lock
		// itself is only the delta. `prev_bid` chains the leg to the
		// previous one when this is a rebid.
		const bidNonce = (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`).toString()
		const bidTemplate: EventTemplate = {
			kind: AUCTION_BID_KIND,
			content: JSON.stringify({
				type: 'auction_bid_v1',
				amount: formData.amount,
				mint: lockResult.mintUrl,
			}),
			tags: buildBidEventTags({
				auctionRootEventId: formData.auctionEventId,
				auctionCoordinate: formData.auctionCoordinates,
				sellerPubkey: formData.sellerPubkey,
				amount: formData.amount,
				mint: lockResult.mintUrl,
				locktime,
				refundPubkey,
				childPubkey,
				lockSecrets,
				proofYs,
				dleqProofs,
				createdForEndAt: formData.auctionEffectiveEndAt,
				bidNonce,
				prevBidId: prevLeg?.bidEventId,
			}),
			created_at: publishedAt,
		}

		// Step 7a — finalize the event fields WITHOUT signing. The NIP-01 event
		// id covers `pubkey`/`created_at`/`kind`/`tags`/`content` and does NOT
		// depend on the signature, so the id computed here is exactly the id the
		// signed event keeps. Finalizing before the publish attempt lets us
		// persist the durable recovery record and the retry cache ahead of any
		// sign/broadcast failure (#1235 Blocking 1).
		//
		// Latent, fail-closed (review 2026-09-18): the frozen id is computed from
		// the template via nostr-tools `getEventHash`, but signing goes through
		// NDK (`signNostrEvent` → `NDKEvent.sign`), whose `toNostrEvent` runs
		// `generateTags()` first and appends a `["client", …]` tag when
		// `ndk.clientName`/`clientNip89` is set. If either were ever set on the
		// singleton, the signed id would differ from this frozen id and the
		// post-sign drift guard below would refuse every bid (fail-closed, never
		// wrong). `clientName` is unset everywhere under `src/` today, so the ids
		// match; derive the id from the signed event, or assert the client tag is
		// absent, if that invariant is ever weakened.
		const unsignedBidEvent: NostrEvent = {
			...bidTemplate,
			pubkey: bidderPubkey,
			created_at: bidTemplate.created_at ?? publishedAt,
			id: '',
			sig: '',
		}
		unsignedBidEvent.id = getEventHash(unsignedBidEvent)
		finalizedBidEventId = unsignedBidEvent.id

		// Step 7b — durable recovery state BEFORE the publish attempt
		// (#1235 Blocking 1, prescription 2). Until this record is written the
		// refund private key exists only in a local variable; if signing or
		// publishing threw first, the locked leg would not even be
		// locktime-reclaimable (the refund branch requires the refund privkey).
		// The record also carries the full locked proofs, so the leg is
		// recoverable from the moment the lock exists — even when publish throws.
		// #1235 follow-up: the write is STRICT (fail-closed) — a storage
		// failure here must abort the publish rather than silently strand
		// the locked leg with no recoverable refund key while the bid still
		// broadcasts.
		// NOTE: bidderRecords is plaintext localStorage; encrypt-at-rest is a
		// named follow-up (review Should-fix 7) and is intentionally out of
		// scope here.
		upsertBidderRecord({
			bidEventId: unsignedBidEvent.id,
			auctionRootEventId: formData.auctionEventId,
			auctionCoordinate: formData.auctionCoordinates,
			sellerPubkey: formData.sellerPubkey,
			p2pkXpub: formData.p2pkXpub,
			derivationPath,
			childPubkey,
			refundPubkey,
			refundPrivateKey,
			mintUrl: lockResult.mintUrl,
			amount: formData.amount, // cumulative bid value
			legLockedAmount: lockResult.amount, // sats actually locked by this leg
			prevBidEventId: prevLeg?.bidEventId ?? null,
			locktime,
			proofs,
			lockSecrets,
			proofYs,
			createdAt: now,
			status: 'live',
		})
		// #1235 round-3 B1 — the full bidder record (refund key + proofs +
		// chain context) now durably supersedes the pre-lock recovery record;
		// drop the latter. (Removal is best-effort — a stale leftover is
		// harmless: it still points at the same refund authority.)
		removePreLockRecoveryRecord(refundPubkey)
		const updatedPendingToken = nip60Actions.updatePendingTokenContext(lockResult.tokenId, {
			kind: 'auction_bid',
			auctionEventId: formData.auctionEventId,
			auctionCoordinates: formData.auctionCoordinates,
			bidEventId: unsignedBidEvent.id,
			sellerPubkey: formData.sellerPubkey,
			pathIssuerPubkey: '',
			lockPubkey: lockResult.lockPubkey,
			refundPubkey: lockResult.refundPubkey,
			locktime: lockResult.locktime,
			derivationPath: lockResult.derivationPath,
			childPubkey: lockResult.childPubkey,
			grantId: lockResult.grantId,
		})
		if (!updatedPendingToken) {
			console.warn('[auctions] Locked auction bid but could not attach bid event id to the local pending lock record before publishing')
		}

		// Step 7c — cache the event so a retry can rebroadcast it VERBATIM
		// (#1235 Blocking 1, prescription 1). The cache entry is written before
		// signing (so even a sign failure is retryable via re-signing the same
		// event) and refreshed with the signature once signing completes.
		cacheAuctionBidEventForRepublish(unsignedBidEvent)
		try {
			const signedBidEvent = await signNostrEvent(bidTemplate)
			// #1235 round-3 B2 — same invariant as republishAuctionBid's
			// post-sign guard: a signer whose identity drifted since
			// `bidderPubkey` was captured would re-key the event and change its
			// id. Refuse to broadcast a foreign event; the cached UNSIGNED event
			// (serialized pre-sign, still keyed to the original id) is preserved.
			if (signedBidEvent.id !== finalizedBidEventId || getEventHash(signedBidEvent) !== finalizedBidEventId) {
				throw new Error(
					`Refusing to publish auction bid: signing changed the event identity (expected ${finalizedBidEventId}, got ${signedBidEvent.id}). Nothing was published.`,
				)
			}
			cacheAuctionBidEventForRepublish(signedBidEvent)
			await publishRequired(signedBidEvent)
		} catch (error) {
			// The recovery record and the signed (or signable) event are already
			// persisted — surface the event id so the funding lifecycle retries
			// with a pure rebroadcast (republishAuctionBid) instead of re-running
			// the lock pipeline (which would swap/lock funds a second time).
			// #1235 round-3 B2: the FINALIZED id (captured pre-sign), never the
			// possibly-drifted signed id — a drift must not poison the retry
			// tracker with a foreign event id.
			throw new AuctionBidPublishFailedError(finalizedBidEventId ?? unsignedBidEvent.id, error)
		}
		// Published — the rebroadcast cache entry is no longer needed.
		discardAuctionBidEventRepublishCacheEntry(unsignedBidEvent.id)

		return unsignedBidEvent.id
	} catch (error) {
		// Tier 1 — already correctly modeled by the inner try above.
		if (error instanceof AuctionBidPublishFailedError) throw error
		// Tier 2 — post-lock but NOT safely publishable: surface the distinct
		// locked-but-unpublished error carrying the lock token id, so the
		// funding lifecycle NEVER falls back to the full re-locking pipeline.
		throw new AuctionBidLockedButUnpublishedError(lockResult.tokenId, error, finalizedBidEventId)
	}
}

/**
 * #1235 Blocking 1: thrown by `publishAuctionBid` when the bid's funds were
 * locked, the durable recovery record was written, and the kind-1023 event was
 * built — but signing or the relay broadcast failed.
 *
 * Carries the bid event id so the funding lifecycle (`useAuctionBidFunding`)
 * can offer an idempotent retry (`republishAuctionBid`) that rebroadcasts the
 * exact persisted signed event instead of re-deriving a fresh path, generating
 * a fresh refund keypair, and re-locking funds at the mint (double-lock).
 */
export class AuctionBidPublishFailedError extends Error {
	/** The kind-1023 event id that failed to broadcast (already persisted and recoverable). */
	public readonly bidEventId: string
	/** The underlying sign/publish failure. */
	public override readonly cause: unknown

	constructor(bidEventId: string, cause: unknown) {
		const causeMessage = cause instanceof Error ? cause.message : String(cause)
		super(`Auction bid ${bidEventId} was funded and signed but could not be published: ${causeMessage}`)
		this.name = 'AuctionBidPublishFailedError'
		this.bidEventId = bidEventId
		this.cause = cause
	}
}

/**
 * #1235 follow-up (post-lock error model): thrown by `publishAuctionBid`
 * when the bid's funds were LOCKED at the mint but the leg never became
 * safely publishable — the kind-1023 event could not be finalized
 * (`toNostrEvent`), the STRICT durable recovery-record write failed
 * (quota / disabled storage / no user scope), or any other post-lock
 * pre-publish step threw before the signed event + recovery record +
 * rebroadcast cache were all in place.
 *
 * Distinct from `AuctionBidPublishFailedError`, whose legs ARE safely
 * retryable by rebroadcast (recovery record + cached signed event already
 * persisted, event id known). A leg carrying THIS error must NEVER fall
 * back to the full re-submit pipeline (`submitPreparedBid` →
 * `publishAuctionBid`) — that would re-derive a fresh path and RE-LOCK the
 * already-locked delta (double-lock). Recovery is RECLAIM-ONLY: the locked
 * proofs live on as a pending token in the NIP-60 wallet (its id is
 * `lockTokenId`), reclaimable via `reclaimToken` after the refund timelock
 * opens.
 *
 * `bidEventId` is set when the event id was finalized before the failure —
 * diagnostics only. It does NOT make the leg rebroadcast-safe: e.g. when
 * the recovery-record write failed, the refund private key is lost and
 * broadcasting the bid would strand the locked leg with no usable refund
 * branch.
 */
export class AuctionBidLockedButUnpublishedError extends Error {
	/** Id of the wallet's pending-token record holding the locked proofs. */
	public readonly lockTokenId: string
	/** Finalized kind-1023 event id when known (diagnostics only; may be null). */
	public readonly bidEventId: string | null
	/** The underlying post-lock failure. */
	public override readonly cause: unknown

	constructor(lockTokenId: string, cause: unknown, bidEventId?: string | null) {
		const causeMessage = cause instanceof Error ? cause.message : String(cause)
		super(
			`Auction bid funds were locked (pending token ${lockTokenId}) but could not be prepared for publishing: ${causeMessage}` +
				(bidEventId ? ` — bid event ${bidEventId}` : '') +
				' — do NOT re-submit this bid; reclaim the locked funds after the refund timelock opens.',
		)
		this.name = 'AuctionBidLockedButUnpublishedError'
		this.lockTokenId = lockTokenId
		this.bidEventId = bidEventId ?? null
		this.cause = cause
	}
}

/**
 * #1235 round-3 B1 — thrown by `publishAuctionBid` when the pre-lock recovery
 * record could not be durably confirmed BEFORE the mint lock call.
 *
 * Provably pre-mint: no swap/lock request was sent, nothing at the mint was
 * mutated. The funding lifecycle's existing bare-error path handles it — a
 * full re-submit is safe once the storage problem is fixed (quota, disabled
 * storage, or no signed-in user scope).
 */
export class AuctionBidPreLockRecordWriteFailedError extends Error {
	/** Refund pubkey of the leg whose recovery record failed to persist. */
	public readonly refundPubkey: string
	/** The underlying persistence failure. */
	public override readonly cause: unknown

	constructor(refundPubkey: string, cause: unknown) {
		const causeMessage = cause instanceof Error ? cause.message : String(cause)
		super(
			`Failed to durably persist the pre-lock recovery record for the auction bid leg (refund pubkey ${refundPubkey}): ${causeMessage}. ` +
				'The mint lock was NOT attempted — no funds were touched. Fix local storage (quota / disabled storage / signed-in state) and place the bid again.',
		)
		this.name = 'AuctionBidPreLockRecordWriteFailedError'
		this.refundPubkey = refundPubkey
		this.cause = cause
	}
}

/**
 * #1235 round-3 B1 — thrown by `publishAuctionBid` when the mint lock's
 * outcome is UNCERTAIN: the NIP-60 store reported
 * `AuctionBidLockMutationPossibleError`, i.e. a swap/lock request may
 * already have been sent to the mint.
 *
 * Sibling (NOT a subclass) of `AuctionBidPublishFailedError` and
 * `AuctionBidLockedButUnpublishedError`: the leg is neither provably
 * pre-mint (so a full re-submit could double-consume inputs) nor provably
 * locked-and-persisted (so there may be nothing to rebroadcast or reclaim).
 * The funding lifecycle must REFUSE the in-session retry and surface the
 * reclaim path: the pre-lock recovery record named by `recoveryRecordId`
 * durably holds the leg's refund private key, and the wallet's pending-token
 * record — when the swap response was processed — makes the leg reclaimable
 * after the refund timelock opens.
 *
 * #1235 round-3 fix 5 (felixfelix #11): the reclaim promise is gated on
 * `pendingTokenPersisted` — the wallet's STRICT pending-token save
 * succeeded. When false the leg is record-only (no durable observation of
 * the mint-issued proofs), so the message must NOT promise a wallet
 * reclaim the app cannot perform; it says the proofs could not be observed
 * and may not be recoverable in-app, and that the recovery record holds the
 * refund key. Honest default: false.
 */
export class AuctionBidLockOutcomeUncertainError extends Error {
	/** Id of the persisted pre-lock recovery record holding the refund authority. */
	public readonly recoveryRecordId: string
	/** Mint the lock request may have been sent to. */
	public readonly mintUrl: string
	/** Amount (sats) the leg attempted to lock. */
	public readonly legAmount: number
	/** Refund pubkey of the uncertain leg. */
	public readonly refundPubkey: string
	/** Whether the wallet durably observed the proofs (STRICT pending-token save succeeded). */
	public readonly pendingTokenPersisted: boolean
	/** The underlying mutation-possible failure. */
	public override readonly cause: unknown

	constructor(params: {
		recoveryRecordId: string
		mintUrl: string
		legAmount: number
		refundPubkey: string
		cause: unknown
		pendingTokenPersisted?: boolean
	}) {
		const causeMessage = params.cause instanceof Error ? params.cause.message : String(params.cause)
		const pendingTokenPersisted = params.pendingTokenPersisted ?? false
		super(
			`The outcome of the auction bid lock is uncertain — a lock request may already have been sent to ${params.mintUrl} ` +
				`for ${params.legAmount} sats (${causeMessage}). No second lock was attempted. A recovery record with the refund key ` +
				`was saved (${params.recoveryRecordId}); ` +
				(pendingTokenPersisted
					? 'the leg may be reclaimable from the wallet once the refund timelock opens.'
					: 'the mint-issued proofs could not be observed by the wallet and may not be recoverable in-app; a recovery record with your refund key was saved.'),
		)
		this.name = 'AuctionBidLockOutcomeUncertainError'
		this.recoveryRecordId = params.recoveryRecordId
		this.mintUrl = params.mintUrl
		this.legAmount = params.legAmount
		this.refundPubkey = params.refundPubkey
		this.pendingTokenPersisted = pendingTokenPersisted
		this.cause = params.cause
	}
}

// ============================================================================
// Idempotent bid-publish retry cache (#1235 Blocking 1)
// ============================================================================
//
// `retryBidPublish` must rebroadcast the EXACT persisted signed kind-1023:
// re-running `publishAuctionBid` would re-derive a fresh path, generate a FRESH
// refund keypair, and re-lock via `lockAuctionBidFunds` (a fresh swap at the
// mint — new proofs, new event id; N retries = N locked legs). Instead,
// `publishAuctionBid` caches the built event here (in-memory + user-scoped
// localStorage) immediately before the first publish attempt, and
// `republishAuctionBid` reads it back and only re-broadcasts:
//
//   - same event id, same tags, same content (integrity-checked by
//     recomputing the event hash);
//   - ZERO additional Cashu swap/lock — no mint interaction at all;
//   - re-signs only when the cached entry predates signing (a sign failure),
//     which does not change the event id and does not touch the mint.
//
// Entries are removed once the event is confirmed published, so the cache
// only ever holds legs whose broadcast is genuinely pending/retryable.

const AUCTION_BID_REPUBLISH_CACHE_KEY = 'auction_bid_republish_events_v1'
const AUCTION_BID_REPUBLISH_CACHE_MAX_ENTRIES = 25

/** Serialized kind-1023 payload persisted for idempotent retry. */
type SerializedAuctionBidEventPayload = NostrEvent

interface CachedAuctionBidEvent {
	bidEventId: string
	payload: SerializedAuctionBidEventPayload
	savedAt: number
}

/** Cache of built-but-not-confirmed-published bid events, keyed by event id. */
type AuctionBidRepublishCache = Record<string, CachedAuctionBidEvent>

// Same-session fallback for when user-scoped localStorage is unavailable
// (e.g. not signed in / storage disabled): an in-session retry must still
// be able to rebroadcast without re-locking.
const auctionBidRepublishMemoryCache: AuctionBidRepublishCache = {}

const loadAuctionBidRepublishCache = (): AuctionBidRepublishCache => {
	const persisted = loadUserData<AuctionBidRepublishCache>(AUCTION_BID_REPUBLISH_CACHE_KEY, {})
	const cache: AuctionBidRepublishCache = {}
	for (const [bidEventId, entry] of Object.entries(persisted ?? {})) {
		if (entry?.bidEventId === bidEventId && entry.payload) cache[bidEventId] = entry
	}
	for (const [bidEventId, entry] of Object.entries(auctionBidRepublishMemoryCache)) {
		if (!cache[bidEventId]) cache[bidEventId] = entry
	}
	return cache
}

const persistAuctionBidRepublishCache = (cache: AuctionBidRepublishCache): void => {
	// Keep the cache bounded — drop the oldest entries first. Retry entries
	// are short-lived (discarded on publish success), so 25 legs is far more
	// than any realistic pending-retry backlog.
	const idsBySavedAt = Object.entries(cache)
		.sort(([, a], [, b]) => a.savedAt - b.savedAt)
		.map(([bidEventId]) => bidEventId)
	while (idsBySavedAt.length > AUCTION_BID_REPUBLISH_CACHE_MAX_ENTRIES) {
		const oldestId = idsBySavedAt.shift()
		if (oldestId === undefined) break
		delete cache[oldestId]
	}
	for (const key of Object.keys(auctionBidRepublishMemoryCache)) delete auctionBidRepublishMemoryCache[key]
	Object.assign(auctionBidRepublishMemoryCache, cache)
	saveUserData(AUCTION_BID_REPUBLISH_CACHE_KEY, cache)
}

const cacheAuctionBidEventForRepublish = (bidEvent: NostrEvent): void => {
	if (!bidEvent.id) return
	const cache = loadAuctionBidRepublishCache()
	cache[bidEvent.id] = { bidEventId: bidEvent.id, payload: bidEvent, savedAt: Date.now() }
	persistAuctionBidRepublishCache(cache)
}

const discardAuctionBidEventRepublishCacheEntry = (bidEventId: string): void => {
	const cache = loadAuctionBidRepublishCache()
	if (!cache[bidEventId]) return
	delete cache[bidEventId]
	persistAuctionBidRepublishCache(cache)
}

/**
 * #1235 Blocking 1: rebroadcast an already-built kind-1023 auction bid event
 * by id — WITHOUT re-deriving the lock path, generating a fresh refund
 * keypair, or touching the mint in any way.
 *
 * Used by the `retryBidPublish` idempotent-retry path in `useAuctionBidFunding`:
 * when a bid was funded (funds locked, recovery record persisted, event built
 * and cached) but the relay broadcast failed, retrying must NOT re-run
 * `publishAuctionBid` — that would lock the delta AGAIN at the mint
 * (double-lock). Instead we re-publish the cached event verbatim: same id,
 * same tags, same content, same signature. Relays that already have it
 * deduplicate; relays that missed it ingest it. No swap, no lock, no signer
 * interaction unless the cached entry predates signing (in which case the
 * event is re-signed locally — the id is unaffected by the signature).
 *
 * @param bidEventId id of the previously-built kind-1023 bid event
 * @returns the rebroadcast bid event id
 * @throws when nothing is cached for the id, the cached payload is not a
 *         kind-1023 bid, the payload does not hash to the requested id
 *         (corrupted/tampered cache), or the rebroadcast fails
 */
export const republishAuctionBid = async (bidEventId: string): Promise<string> => {
	if (!bidEventId) throw new Error('Cannot rebroadcast auction bid: bidEventId is empty')
	const cached = loadAuctionBidRepublishCache()[bidEventId]
	if (!cached) {
		throw new Error(
			`No cached bid event for ${bidEventId} — cannot rebroadcast. Do not retry the full publish pipeline: the funds for this leg are already locked.`,
		)
	}

	let bidEvent = cached.payload
	if (bidEvent.kind !== AUCTION_BID_KIND) {
		throw new Error(`Refusing to rebroadcast ${bidEventId}: expected kind ${AUCTION_BID_KIND}, got kind ${bidEvent.kind}`)
	}
	// Integrity: the cached payload must still hash to the requested id —
	// guards against a corrupted or tampered cache entry.
	if (getEventHash(bidEvent) !== bidEventId || bidEvent.id !== bidEventId) {
		throw new Error(`Refusing to rebroadcast ${bidEventId}: cached payload does not hash to the requested event id`)
	}

	if (!bidEvent.sig) {
		// #1235 round-3 B2 — retry identity binding. The cached unsigned event
		// was authored by the original bidder; signing overwrites the event's
		// pubkey with the active signer's user, so a retry from a DIFFERENT
		// account would publish a foreign-authored kind-1023 carrying the
		// original bidder's lock secrets under a drifted event id. Refuse
		// PRE-SIGN — zero mint interaction, zero signing, cache entry preserved.
		const activeUser = await getUser()
		if (activeUser?.pubkey !== bidEvent.pubkey) {
			throw new Error(
				`Refusing to republish auction bid ${bidEventId}: cached bid was created by ${bidEvent.pubkey} ` +
					`but the active signer is ${activeUser?.pubkey ?? ''}. Switch back to the original bidder account and retry. ` +
					`No signing, no publishing, and no mint interaction was performed; the cached event is preserved.`,
			)
		}
		// Cached between event construction and signing (a sign failure).
		// Signing is local, does not touch the mint, and does not change the
		// event id — this stays idempotent… unless the signer's identity drifts
		// between the pre-sign guard above and `sign`'s internal `author`
		// assignment (a stateful or remote signer). Belt-and-braces: recompute
		// the hash/id and require equality with the ORIGINAL event id before
		// publishing anything.
		const signed = await signNostrEvent(bidEvent)
		if (getEventHash(signed) !== bidEventId || signed.id !== bidEventId) {
			throw new Error(
				`Refusing to republish auction bid ${bidEventId}: re-signing produced a different event id (${signed.id}). Nothing was published; the cached entry is preserved.`,
			)
		}
		cacheAuctionBidEventForRepublish(signed)
		bidEvent = signed
	}

	try {
		await publishRequired(bidEvent)
	} catch (error) {
		throw new AuctionBidPublishFailedError(bidEventId, error)
	}
	// Published — the rebroadcast cache entry is no longer needed.
	discardAuctionBidEventRepublishCacheEntry(bidEventId)
	return bidEvent.id
}

const bytesToLowerHex = (bytes: Uint8Array): string => {
	let out = ''
	for (let i = 0; i < bytes.length; i++) out += bytes[i].toString(16).padStart(2, '0')
	return out
}

// ============================================================================
// Phase 5 — Bidder kind-1025 path release (AUCTIONS.md §4.3.1)
// ============================================================================

export interface PublishBidderPathReleaseInput {
	/** kind-1023 bid event id this release applies to. */
	bidEventId: string
	/** Why we're releasing. Default 'settlement' (we won). */
	releaseReason?: PathReleaseReason
	/** Optional validator verdict event ids the bidder is responding to. */
	auditorRefs?: string[]
	/** Optional kind-1026 fallback offer this release accepts. */
	fallbackOfferId?: string
	/** Free-form human note for the event content. */
	note?: string
}

export interface PublishBidderPathReleaseResult {
	/**
	 * kind-1025 event id from the LATEST leg in the chain — the one the
	 * seller's kind-1024 will reference via `path_release`. For
	 * single-leg bids there's exactly one. For rebid chains the older
	 * legs' kind-1025s are also published but not returned here; query
	 * the relay to retrieve them.
	 */
	pathReleaseEventId: string
	/** Released path for the latest leg (diagnostics; do not display in UI). */
	derivationPath: string
	/** Number of kind-1025 events published in this call (= legs in chain). */
	legsReleased: number
	/** Cumulative bid value the chain represents. Sum of every leg's lock. */
	cumulativeBidAmount: number
}

/**
 * Bidder-side "settle" action — AUCTIONS.md §4.3.1.
 *
 * Publishes a kind-1025 path release for one of our own bids. The
 * seller can then derive `seller_child_privkey = derive(seller_xpriv,
 * path)` and redeem the locked proofs at the mint. After we publish:
 *   - validators observe the kind-1025, verify
 *     `derive(p2pk_xpub, path) == child_pubkey`, and flip the bid's
 *     verdict to `settled_promptly` (or `fraudulent_bid` if the
 *     derivation doesn't match, which would only happen if our local
 *     record is corrupted).
 *   - the seller's settlement client picks up the release and runs
 *     redemption.
 *
 * Pre-publish we verify the local record's `derive(p2pk_xpub, path) ==
 * child_pubkey` ourselves: if it doesn't match, the path or the
 * child_pubkey in storage is corrupted and publishing a kind-1025 would
 * only produce a `fraudulent_bid` on our own reputation. Failing fast
 * locally is the right call.
 *
 * Idempotency: if the record is already `settled`, returns the existing
 * kind-1025 isn't tracked — caller can re-publish if they want a fresh
 * event, but the typical path returns early without re-emitting.
 */
export const publishBidderPathRelease = async (input: PublishBidderPathReleaseInput): Promise<PublishBidderPathReleaseResult> => {
	if (!input.bidEventId) throw new Error('bidEventId is required')

	// Walk the rebid chain. For a single-leg bid this returns one
	// record. For a rebid chain, oldest→newest, every leg the bidder
	// locked. Each leg has its own derivation_path + cashu_token; the
	// seller redeems them all on settle.
	const chain = walkBidderRecordChain(input.bidEventId)
	if (chain.length === 0) {
		throw new Error(
			`No local bidder record for bid ${input.bidEventId}. The bidder client must hold the derivation path to settle; lost record = unsettleable.`,
		)
	}
	const latestLeg = chain[chain.length - 1]

	// Pre-publish sanity for every leg. If any leg's derivation is
	// corrupted, refuse the whole release — the seller would fail on
	// that leg and validators would mark the bid `fraudulent_bid`.
	// Better to surface the local-storage corruption than burn
	// reputation.
	for (const leg of chain) {
		const derivedChild = deriveAuctionChildP2pkPubkeyFromXpub(leg.p2pkXpub, leg.derivationPath)
		if (derivedChild.toLowerCase() !== leg.childPubkey.toLowerCase()) {
			throw new Error(
				`Refusing to publish chain release: leg ${leg.bidEventId.slice(0, 8)}… derive(p2pk_xpub, path) = ${derivedChild} does not match stored child_pubkey ${leg.childPubkey}. Local bidder record is corrupted.`,
			)
		}
	}

	// Re-resolve the canonical winner immediately before publishing the path.
	// Render-time checks and caller assertions can be stale: a newer bid,
	// terminal settlement, or the locktime may have changed since the UI read.
	try {
		const [
			{ fetchAuction, fetchAuctionBids, fetchAuctionSettlements, fetchAuctionVerdicts },
			{ parseAuctionEvent },
			{ parseBidEvent },
			{ parseSettlementEvent },
			{ parseValidatorVerdictEvent },
			{ computeValidatedBids },
		] = await Promise.all([
			import('@/queries/auctions'),
			import('@/lib/schemas/auction/auctionEvent'),
			import('@/lib/schemas/auction/bidEvent'),
			import('@/lib/schemas/auction/settlementEvents'),
			import('@/lib/schemas/auction/validatorEvents'),
			import('@/lib/auction/bidValidation'),
		])
		const auctionEvent = await fetchAuction(latestLeg.auctionRootEventId, true)
		if (!auctionEvent) throw new Error('Auction no longer exists')
		const parsedAuctionResult = parseAuctionEvent(toRawEvent(auctionEvent))
		if (!parsedAuctionResult.ok) throw new Error('Auction is malformed')
		const parsedAuction = parsedAuctionResult.value
		const now = Math.floor(Date.now() / 1000)
		const settlementDeadline = parsedAuction.maxEndAt + parsedAuction.settlementGrace
		if (now >= settlementDeadline) throw new Error('Settlement window has expired')

		const [bidEvents, verdictEvents, settlementEvents] = await Promise.all([
			fetchAuctionBids(latestLeg.auctionRootEventId, null, parsedAuction.coordinate, true),
			fetchAuctionVerdicts(latestLeg.auctionRootEventId, null, parsedAuction.coordinate, parsedAuction.auditors, undefined),
			fetchAuctionSettlements(latestLeg.auctionRootEventId, null, parsedAuction.coordinate, undefined, true),
		])
		const parsedBids = bidEvents
			.map((event) => parseBidEvent(toRawEvent(event)))
			.filter((result): result is { ok: true; value: import('@/lib/auction/events').ParsedBidEvent } => result.ok)
			.map((result) => result.value)
		const parsedVerdicts = verdictEvents
			.map((event) => parseValidatorVerdictEvent(toRawEvent(event)))
			.filter((result): result is { ok: true; value: import('@/lib/auction/events').ParsedValidatorVerdictEvent } => result.ok)
			.map((result) => result.value)
		// ADR-0011 review R2: thread the DLEQ keysets here too. DLEQ is
		// unconditional, so without evidence `computeValidatedBids` classifies
		// every published bid as pending, `canonicalWinner` is null, and this
		// guard throws "Auction winner changed" for every DLEQ-bearing bid —
		// making the bidder's half of settlement (the path release the seller
		// needs to derive `seller_child_privkey`) impossible. The settlement and
		// reserve_not_met siblings already gather the same map.
		const releaseAcq = await fetchDleqKeysetsForBidsDetailed(parsedBids, parsedAuction.mints)
		const validated = computeValidatedBids({
			auction: parsedAuction,
			bids: parsedBids,
			verdicts: parsedVerdicts,
			postSettlement: false,
			dleqKeysets: releaseAcq.keysets,
			dleqUnknownKeysets: releaseAcq.unknownKeysets,
		})
		if (!validated.canonicalWinner || validated.canonicalWinner.id !== input.bidEventId) {
			throw new Error('Auction winner changed. Refresh and try again.')
		}
		const hasAuctionSettlement = settlementEvents.some((event) => {
			const parsedSettlementResult = parseSettlementEvent(toRawEvent(event))
			if (!parsedSettlementResult.ok) return false
			const settlement = parsedSettlementResult.value
			return (
				settlement.sellerPubkey.toLowerCase() === parsedAuction.sellerPubkey.toLowerCase() &&
				settlement.auctionRootEventId === parsedAuction.rootEventId &&
				settlement.auctionCoordinate === parsedAuction.coordinate
			)
		})
		if (hasAuctionSettlement) {
			throw new Error('Auction already has a settlement. Refresh and try again.')
		}
	} catch (err) {
		throw new Error(`Cannot release path: ${err instanceof Error ? err.message : String(err)}`)
	}

	// Verify won_pending_settlement quorum before releasing the path.
	// The bidder should not release their locked ecash unless a quorum
	// of auditors has confirmed that this bid is the canonical winner
	// and the auction is ready for settlement. This prevents premature
	// release based on stale or incomplete information.
	try {
		const [{ fetchAuctionVerdicts, fetchAuction }, { parseValidatorVerdictEvent }, { parseAuctionEvent }] = await Promise.all([
			import('@/queries/auctions'),
			import('@/lib/schemas/auction/validatorEvents'),
			import('@/lib/schemas/auction/auctionEvent'),
		])
		const auctionEvent = await fetchAuction(latestLeg.auctionRootEventId, true)
		if (!auctionEvent) throw new Error('Auction no longer exists')
		const parsedAuctionResult = parseAuctionEvent(toRawEvent(auctionEvent))
		if (!parsedAuctionResult.ok) throw new Error('Auction is malformed')
		const quorumAuction = parsedAuctionResult.value
		const verdictEvents = await fetchAuctionVerdicts(
			latestLeg.auctionRootEventId,
			null,
			latestLeg.auctionCoordinate,
			quorumAuction.auditors,
			undefined,
		)
		const parsedVerdicts = verdictEvents
			.map((v) => parseValidatorVerdictEvent(toRawEvent(v)))
			.filter((r): r is { ok: true; value: import('@/lib/auction/events').ParsedValidatorVerdictEvent } => r.ok)
			.map((r) => r.value)
			.filter((v) => v.bidEventId === input.bidEventId && v.claim === 'won_pending_settlement')

		const confirmingAuditors = new Set(parsedVerdicts.map((v) => v.validatorPubkey))
		const auditorCount = quorumAuction.auditors.filter((a) => confirmingAuditors.has(a)).length
		if (auditorCount < quorumAuction.auditorQuorum) {
			throw new Error(
				`Cannot release path: only ${auditorCount}/${quorumAuction.auditors.length} auditors confirmed ` +
					`won_pending_settlement (quorum requires ${quorumAuction.auditorQuorum}). Wait for more validators.`,
			)
		}
	} catch (err) {
		// Quorum verification is a hard gate for path release (M2): the
		// release exposes the bidder's derivation path + locked Cashu token,
		// and once published the seller can derive the child key and redeem
		// the proofs directly at the mint — bypassing every downstream
		// canonical-winner check in the settlement publisher. Authorization
		// unavailable ≠ authorization granted: ANY failure to positively
		// establish the canonical auction + won_pending_settlement quorum
		// (network error, parse error, auction lookup failure) keeps the
		// gate CLOSED. There is no post-close exception — a winner whose
		// relays are unreachable waits and retries, and retains the
		// locktime refund branch as the safe fallback.
		if (err instanceof Error && err.message.includes('quorum requires')) throw err

		throw new Error(
			'Cannot release path: quorum verification failed due to a network or parse error. ' +
				'The quorum gate must succeed before releasing locked proofs. ' +
				'Please retry when relays are available.',
		)
	}

	const releaseReason: PathReleaseReason = input.releaseReason ?? 'settlement'

	let latestEventId = ''
	let latestDerivationPath = ''
	let cumulative = 0

	for (const leg of chain) {
		// Encode this leg's locked Cashu token so the seller can decode
		// + redeem. Proofs are P2PK-locked to derive(p2pk_xpub, path) —
		// only the seller (who holds `seller_xpriv`) can spend, so
		// publishing publicly is safe.
		let cashuToken: string
		try {
			if (!leg.proofs || leg.proofs.length === 0) {
				throw new Error(`leg ${leg.bidEventId.slice(0, 8)}… has no proofs in local record`)
			}
			cashuToken = getEncodedToken({ mint: leg.mintUrl, proofs: leg.proofs })
		} catch (err) {
			throw new Error(
				`Failed to encode Cashu token for leg ${leg.bidEventId.slice(0, 8)}…: ${err instanceof Error ? err.message : String(err)}`,
			)
		}

		const template: EventTemplate = {
			kind: AUCTION_PATH_RELEASE_KIND as unknown as number,
			content: input.note ?? '',
			tags: buildPathReleaseTags({
				bidEventId: leg.bidEventId,
				auctionCoordinate: leg.auctionCoordinate,
				sellerPubkey: leg.sellerPubkey,
				derivationPath: leg.derivationPath,
				childPubkey: leg.childPubkey,
				releaseReason,
				// Only the latest leg carries auditorRefs / fallbackOfferId —
				// those reference verdicts/offers about the chain's current
				// state, not its history.
				auditorRefs: leg.bidEventId === input.bidEventId ? input.auditorRefs : undefined,
				fallbackOfferId: leg.bidEventId === input.bidEventId ? input.fallbackOfferId : undefined,
				cashuToken,
			}),
			created_at: Math.floor(Date.now() / 1000),
		}

		const event = await signNostrEvent(template)
		await publishRequired(event)

		updateBidderRecordStatus(leg.bidEventId, 'settled')

		latestEventId = event.id
		latestDerivationPath = leg.derivationPath
		cumulative += leg.legLockedAmount
	}

	return {
		pathReleaseEventId: latestEventId,
		derivationPath: latestDerivationPath,
		legsReleased: chain.length,
		cumulativeBidAmount: cumulative,
	}
}

export const usePublishAuctionBidMutation = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: async (formData: AuctionBidFormData) => publishAuctionBid(formData),
		onSuccess: async (_eventId, variables) => {
			await queryClient.invalidateQueries({ queryKey: auctionKeys.bids(variables.auctionEventId) })
			await queryClient.invalidateQueries({ queryKey: auctionKeys.details(variables.auctionEventId) })
			await queryClient.invalidateQueries({ queryKey: auctionKeys.all })
		},
		onError: (error) => {
			console.error('Failed to publish auction bid:', error)
			toast.error(`Failed to submit bid: ${error instanceof Error ? error.message : String(error)}`)
		},
	})
}

/**
 * Mutation wrapper for `republishAuctionBid` — the idempotent retry path for a
 * bid whose funds are already locked and whose kind-1023 event was built and
 * cached but whose relay broadcast failed (#1235 Blocking 1). Unlike
 * `usePublishAuctionBidMutation`, this NEVER re-locks funds: it only
 * rebroadcasts the exact persisted signed event by id.
 */
export const useRepublishAuctionBidMutation = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: async (bidEventId: string) => republishAuctionBid(bidEventId),
		onSuccess: async (bidEventId) => {
			// The rebroadcast leg's bid may now be visible to bidders queries.
			// Cache invalidation is a UI-refresh aid, not proof of relay
			// acceptance (src/publish/AGENTS.md).
			void bidEventId
			await queryClient.invalidateQueries({ queryKey: auctionKeys.all })
		},
		onError: (error) => {
			// Toasting happens in the funding lifecycle's retry handler so the
			// user gets exactly one message per retry attempt.
			console.error('Failed to republish auction bid:', error)
		},
	})
}

// ============================================================================
// Phase 6 — Seller kind-1024 settlement (AUCTIONS.md §4.3.2 / §8.1)
// ============================================================================
//
// Flow (happy path):
//   1. Fetch the auction event (need p2pk_xpub, max_end_at, settlement_grace,
//      coordinate, root event id).
//   2. Fetch the bids on the auction; pick the winning bid (highest amount
//      in the valid window).
//   3. Fetch the kind-1025 path release for that winning bid. Refuse to
//      settle without one.
//   4. Verify derive(p2pk_xpub, release.derivation_path) === bid.child_pubkey;
//      mismatch means the bid was fraudulent (lock pubkey not actually a
//      child of the seller's xpub) — caller falls back to the next bid.
//   5. Derive seller_child_privkey via the wallet's HD account.
//   6. Decode the cashu_token from the path release; redeem at the mint
//      (NIP-60 wallet's receiveLockedEcash handles the swap into the
//      seller's wallet state).
//   7. Publish the kind-1024 settlement event with payout + path_release
//      refs.
//
// Non-happy paths the form can also drive (status override):
//   - 'reserve_not_met' — no redemption; just publish a kind-1024 noting
//     status and let losers self-refund at locktime.
//
// Fallback chains, cancellations, multi-validator quorum — all out of
// scope for the MVP. The UI's only settle button this milestone is "I
// won, here's the path / I have a path, redeem".

export const publishAuctionSettlement = async (formData: AuctionSettlementFormData): Promise<string> => {
	if (!formData.auctionEventId) throw new Error('Auction event id is required')

	// Lazy imports to avoid pulling settlement-only deps into the bid
	// path's bundle.
	const [
		{ fetchAuction, fetchAuctionBids, fetchAuctionPathReleases, fetchAuctionSettlements, fetchAuctionVerdicts, getBidAmount },
		auctionSettlementMod,
		settlementEventsMod,
		constantsMod,
		auctionEventMod,
		bidEventMod,
		validatorEventsMod,
		bidValidationMod,
		nut7Mod,
	] = await Promise.all([
		import('@/queries/auctions'),
		import('@/lib/auctionSettlement'),
		import('@/lib/schemas/auction/settlementEvents'),
		import('@/lib/auction/constants'),
		import('@/lib/schemas/auction/auctionEvent'),
		import('@/lib/schemas/auction/bidEvent'),
		import('@/lib/schemas/auction/validatorEvents'),
		import('@/lib/auction/bidValidation'),
		import('@/lib/cashu/nut7'),
	])
	const { getAuctionTagValue: getTag, AUCTION_SETTLEMENT_KIND: kind1024 } = auctionSettlementMod
	const { parsePathReleaseEvent, parseSettlementEvent } = settlementEventsMod
	const { AUCTION_SETTLEMENT_POLICY: policyV1 } = constantsMod
	const { parseAuctionEvent } = auctionEventMod
	const { parseBidEvent } = bidEventMod
	const { parseValidatorVerdictEvent } = validatorEventsMod
	const { computeValidatedBids, validateBidChainNut7PrePublish } = bidValidationMod
	const { checkProofStateBatch, aggregateBidNut7State } = nut7Mod

	// 1. Auction event.
	const auctionEvent = await fetchAuction(formData.auctionEventId, true)
	if (!auctionEvent) throw new Error(`Auction ${formData.auctionEventId} not found on relay`)
	const sellerPubkey = auctionEvent.pubkey
	const activeUser = await getUser()
	if (activeUser?.pubkey !== sellerPubkey) {
		throw new Error('Only the auction seller can publish a kind-1024 settlement event')
	}
	const auctionDTag = getTag(auctionEvent, 'd')?.trim()
	const auctionCoordinate = formData.auctionCoordinates?.trim() || (auctionDTag ? `${30408}:${sellerPubkey}:${auctionDTag}` : '')
	if (!auctionCoordinate) {
		throw new Error('Auction coordinate is required to query kind-1025 path releases')
	}
	const auctionRootEventId = getTag(auctionEvent, 'auction_root_event_id') ?? auctionEvent.id
	const p2pkXpub = getTag(auctionEvent, 'p2pk_xpub') ?? ''
	const declaredPolicy = getTag(auctionEvent, 'settlement_policy')
	if (declaredPolicy && declaredPolicy !== policyV1) {
		throw new Error(`Auction settlement_policy is ${declaredPolicy}; this client only handles ${policyV1}`)
	}

	// 1b. Parse the auction event for structured access to maxEndAt, reserve, etc.
	const parsedAuctionResult = parseAuctionEvent(toRawEvent(auctionEvent))
	if (!parsedAuctionResult.ok) {
		throw new Error(`Auction event is malformed: ${'error' in parsedAuctionResult ? parsedAuctionResult.error.message : 'unknown'}`)
	}
	const parsedAuction = parsedAuctionResult.value

	// 1c. Verify the auction has ended. Settlement before max_end_at is invalid.
	const now = Math.floor(Date.now() / 1000)
	if (now < parsedAuction.maxEndAt) {
		throw new Error(`Auction has not ended yet (max_end_at=${parsedAuction.maxEndAt}, now=${now}). Cannot settle before auction close.`)
	}

	const closeAt = now

	// Winner derivation must be based on mint-reported proof states, never on
	// defaulted or fabricated ones: an unconfirmed NUT-7 state stays unconfirmed
	// (`bid_pending_review`), and only the mint's response converts it.
	// Query each allowlisted mint for every parsed bid's proof_y set and
	// aggregate per bid (any spent → spent, all unspent → unspent, else
	// pending). Bids on non-allowlisted mints are never polled: they cannot
	// win (validateBid rejects them via the mint-allowlist check), and polling
	// attacker-supplied mint URLs from raw bids would turn the client into a
	// beacon. Unreachable mints leave their bids 'unknown' → pending.
	type ParsedBidEventT = import('@/lib/auction/events').ParsedBidEvent
	type Nut7ProofStateT = import('@/lib/auction/constants').Nut7ProofState
	const fetchNut7StatesForBids = async (bidList: ParsedBidEventT[]): Promise<Map<string, Nut7ProofStateT>> => {
		const states = new Map<string, Nut7ProofStateT>()
		const byMint = new Map<string, ParsedBidEventT[]>()
		for (const b of bidList) {
			if (!parsedAuction.mints.includes(b.mint)) continue
			const arr = byMint.get(b.mint) ?? []
			arr.push(b)
			byMint.set(b.mint, arr)
		}
		for (const [mint, mintBids] of byMint) {
			const perProof = await checkProofStateBatch(
				mint,
				mintBids.flatMap((b: ParsedBidEventT) => b.proofYs),
			)
			for (const b of mintBids) {
				states.set(b.id, aggregateBidNut7State(perProof, b.proofYs) ?? 'unknown')
			}
		}
		return states
	}

	// 2. Resolve `reserve_not_met` shortcut. Redemption work happens only on
	// the settled path — losers self-refund at locktime via their refund
	// branch. Mint I/O here is limited to NUT-7 evidence for the reserve
	// decision above.
	if (formData.status === 'reserve_not_met') {
		// B3: Before publishing reserve_not_met, verify no reserve-meeting
		// canonical winner exists. A seller who has already redeemed a
		// winning bid must not be able to displace it with reserve_not_met.
		const [rnmBids, rnmVerdicts] = await Promise.all([
			fetchAuctionBids(formData.auctionEventId, null, auctionCoordinate, true),
			fetchAuctionVerdicts(formData.auctionEventId, null, auctionCoordinate, undefined, undefined, true),
		])
		const rnmParsedBids = rnmBids
			.map((b) => parseBidEvent(toRawEvent(b)))
			.filter((r): r is { ok: true; value: import('@/lib/auction/events').ParsedBidEvent } => r.ok)
			.map((r) => r.value)
		const rnmParsedVerdicts = rnmVerdicts
			.map((v) => parseValidatorVerdictEvent(toRawEvent(v)))
			.filter((r): r is { ok: true; value: import('@/lib/auction/events').ParsedValidatorVerdictEvent } => r.ok)
			.map((r) => r.value)

		const rnmNut7States = await fetchNut7StatesForBids(rnmParsedBids)
		// ADR-0011: DLEQ is unconditional, so the reserve_not_met path always
		// gathers keyset evidence — otherwise every bid stays pending.
		const rnmAcq = await fetchDleqKeysetsForBidsDetailed(rnmParsedBids, parsedAuction.mints)
		const rnmValidated = computeValidatedBids({
			auction: parsedAuction,
			bids: rnmParsedBids,
			verdicts: rnmParsedVerdicts,
			nut7States: rnmNut7States,
			postSettlement: false,
			dleqKeysets: rnmAcq.keysets,
			dleqUnknownKeysets: rnmAcq.unknownKeysets,
		})

		// ADR-0011 review A2 (PR #1280 discussion_r3999446834): the guard must
		// consider bids whose DLEQ evidence is UNRESOLVED, not only the canonical
		// winner. A quorum-confirmed, structurally valid reserve-meeting bid is
		// demoted to `pending` when its mint keyset fetch fails, so it does NOT
		// appear in `canonicalWinner`; a winner-only check therefore reads the
		// auction as reserve-unmet and lets the seller publish a terminal state
		// that contradicts a bid which may still become valid. Evidence
		// unavailability is a RETRY signal, never a licence to publish.
		const rnmGuard = evaluateReserveNotMetGuard(rnmValidated, parsedAuction.reserve)
		// ADR-0011 review R3: a permanently-`pending` reserve-meeting bid (mint
		// outage, or a crafted bid naming an unadvertised keyset) must not hold
		// the seller hostage forever. The seller can opt into publishing the
		// terminal state; the reason is recorded on the event so the override is
		// auditable. This never bypasses the `blocked` case (a validated
		// reserve-meeting winner), only `evidence-unavailable`. The decision is a
		// pure helper (`resolveReserveNotMetPublish`) so it is unit-tested.
		const rnmDecision = resolveReserveNotMetPublish(rnmGuard, formData.dleqEvidenceOverride === true)
		if (rnmDecision.action === 'throw') throw new Error(rnmDecision.message)
		const rnmOverrideReason = rnmDecision.overrideReason

		// Terminal-event consistency: refuse reserve_not_met when a
		// structurally-valid `settled` settlement already exists for this
		// auction — the seller cannot displace a completed settlement with a
		// later terminal event. (Structural check only: seller + auction
		// refs; deeper completeness validation happens on the read path.)
		const existingSettlements = await fetchAuctionSettlements(formData.auctionEventId, null, auctionCoordinate, undefined, true)
		const hasSettledSettlement = existingSettlements.some((s) => {
			const parsed = parseSettlementEvent(toRawEvent(s))
			if (!parsed.ok) return false
			return isStructurallyValidSettledSettlement(parsed.value, parsedAuction)
		})
		if (hasSettledSettlement) {
			throw new Error('Cannot publish reserve_not_met: a settled settlement already exists for this auction.')
		}

		const template: EventTemplate = {
			kind: AUCTION_SETTLEMENT_KIND,
			content: '',
			tags: (await import('@/lib/auction/tagBuilders')).buildSettlementTags({
				auctionRootEventId,
				auctionCoordinate,
				status: 'reserve_not_met',
				closeAt,
				finalAmount: 0,
				reason: formData.reason ?? rnmOverrideReason ?? 'reserve_not_met',
			}),
			created_at: Math.floor(Date.now() / 1000),
		}
		const event = await signNostrEvent(template)
		await publishRequired(event)
		return event.id
	}

	// 3. Bids → winning bid. Fetch bids + verdicts, parse, and call
	// computeValidatedBids to get the canonical winner. The publisher
	// does not trust the caller's assertion of who won — it derives the
	// winner independently from validator quorum evidence.
	const [bids, verdictEvents] = await Promise.all([
		fetchAuctionBids(formData.auctionEventId, null, auctionCoordinate, true),
		fetchAuctionVerdicts(formData.auctionEventId, null, auctionCoordinate, undefined, undefined, true),
	])
	if (!bids.length) {
		throw new Error('No bids on this auction — nothing to settle. Use reserve_not_met to close it.')
	}

	const parsedBids = bids
		.map((b) => parseBidEvent(toRawEvent(b)))
		.filter((r): r is { ok: true; value: import('@/lib/auction/events').ParsedBidEvent } => r.ok)
		.map((r) => r.value)
	const parsedVerdicts = verdictEvents
		.map((v) => parseValidatorVerdictEvent(toRawEvent(v)))
		.filter((r): r is { ok: true; value: import('@/lib/auction/events').ParsedValidatorVerdictEvent } => r.ok)
		.map((r) => r.value)

	const nut7States = await fetchNut7StatesForBids(parsedBids)
	// ADR-0011: the settlement path always gathers the DLEQ keysets — without
	// the evidence, `computeValidatedBids` treats every bid as pending and the
	// seller cannot settle (fail-safe, not fail-open).
	const settlementAcq = await fetchDleqKeysetsForBidsDetailed(parsedBids, parsedAuction.mints)
	const validatedBids = computeValidatedBids({
		auction: parsedAuction,
		bids: parsedBids,
		verdicts: parsedVerdicts,
		nut7States,
		postSettlement: false,
		dleqKeysets: settlementAcq.keysets,
		dleqUnknownKeysets: settlementAcq.unknownKeysets,
	})

	if (!validatedBids.canonicalWinner) {
		if (validatedBids.validBids.length === 0 && parsedAuction.reserve > 0) {
			throw new Error('No validated winner. If the reserve was not met, use status=reserve_not_met to close the auction.')
		}
		throw new Error('No validated winner — all bids are pending or invalid. Cannot settle.')
	}

	// Cross-check: if the caller specified a winningBidEventId, verify it
	// matches the canonical winner derived from validator quorum.
	if (formData.winningBidEventId && formData.winningBidEventId !== validatedBids.canonicalWinner.id) {
		throw new Error(
			`Winning bid mismatch: caller expected ${formData.winningBidEventId}, ` +
				`but validator quorum identifies ${validatedBids.canonicalWinner.id} as the canonical winner.`,
		)
	}

	// Verify reserve price is met.
	if (parsedAuction.reserve > 0 && validatedBids.canonicalWinner.amount < parsedAuction.reserve) {
		throw new Error(
			`Winning bid (${validatedBids.canonicalWinner.amount} sats) is below reserve (${parsedAuction.reserve} sats). Use status=reserve_not_met to close the auction.`,
		)
	}

	// Map canonical winner back to its raw event for chain walking.
	const winningBid = bids.find((b) => b.id === validatedBids.canonicalWinner!.id)!
	const winningBidId = winningBid.id
	const winnerPubkey = winningBid.pubkey
	const winningAmount = validatedBids.canonicalWinner.amount
	const childPubkeyFromBid = (getTag(winningBid, 'child_pubkey') ?? '').toLowerCase()
	const mintUrl = getTag(winningBid, 'mint') ?? ''
	if (!mintUrl) throw new Error('Winning bid is missing its `mint` tag — cannot redeem')

	// 4. Walk the rebid chain. The winning bid may be a rebid; if so it
	// has a `prev_bid` tag pointing at the previous leg, which in turn
	// may chain back further. Each leg is locked at its OWN
	// derivation_path with its OWN delta amount. To redeem the full
	// cumulative bid the seller must redeem every leg in the chain.
	//
	// AUCTIONS.md §8.1 line 1014: "derive every child privkey in the
	// winner's chain, swap each leg at the mint".
	const bidsById = new Map(bids.map((b) => [b.id, b]))
	const chainBids: NostrEventLike[] = []
	const seenIds = new Set<string>()
	let cursor: string | undefined = winningBidId
	while (cursor) {
		if (seenIds.has(cursor)) throw new Error(`prev_bid cycle detected at ${cursor.slice(0, 8)}…`)
		seenIds.add(cursor)
		const leg = bidsById.get(cursor)
		if (!leg) throw new Error(`Chain leg ${cursor.slice(0, 8)}… not found in fetched bids; relay set incomplete?`)
		chainBids.unshift(leg) // oldest → newest
		cursor = getTag(leg, 'prev_bid') || undefined
	}
	if (chainBids.length === 0) throw new Error('Empty chain — should be impossible')

	// 4b. Pre-publish NUT-7 gate: before committing to an irreversible
	// settlement, verify every leg in the winning bid chain has confirmed
	// unspent proofs. A `spent` state = pre-settlement double-spend fraud.
	// Missing/unknown = the mint hasn't confirmed — the seller should retry.
	// This gate is stricter than the read/display path (computeValidatedBids
	// with skipNut7Check) because publishing a settlement is irreversible.
	const parsedChainBids = chainBids
		.map((b) => parseBidEvent(b))
		.filter((r): r is { ok: true; value: import('@/lib/auction/events').ParsedBidEvent } => r.ok)
		.map((r) => r.value)
	if (parsedChainBids.length !== chainBids.length) {
		throw new Error(
			`Chain leg parse mismatch: ${chainBids.length} raw legs but only ${parsedChainBids.length} parsed. ` +
				`Cannot safely settle — a leg may have a malformed structure that bypasses the NUT-7 fraud check.`,
		)
	}
	const nut7Error = validateBidChainNut7PrePublish(parsedChainBids, nut7States, now)
	if (nut7Error) throw new Error(nut7Error)

	// 5. Path releases — collect a kind-1025 for every leg. The bidder
	// publishes one per leg as part of `publishBidderPathRelease`.
	// Refuse to settle if any leg is missing its release: a partial
	// chain can't be redeemed and the seller would only end up with
	// some of the bid value.
	const allReleases = await fetchAuctionPathReleases(formData.auctionEventId, 500, auctionCoordinate)
	const releasesByBidId = new Map<string, NostrEventLike[]>()
	for (const ev of allReleases) {
		const e = getTag(ev, 'e') ?? ''
		if (!e) continue
		const arr = releasesByBidId.get(e) ?? []
		arr.push(ev)
		releasesByBidId.set(e, arr)
	}

	interface ResolvedLeg {
		bid: NostrEventLike
		releaseEventId: string
		derivationPath: string
		bidChildPubkey: string
		releaseChildPubkey: string
		childPubkey: string
		mintUrl: string
		cashuToken: string
		legAmount: number // sats this leg specifically contributes (delta)
	}

	const resolvedLegs: ResolvedLeg[] = []
	let runningCumulative = 0
	for (const legBid of chainBids) {
		const legReleases = releasesByBidId.get(legBid.id) ?? []
		if (!legReleases.length) {
			throw new Error(
				`Chain leg ${legBid.id.slice(0, 8)}… (amount ${getBidAmount(legBid)} sats) has no kind-1025 path release. The bidder must publish a release for every leg in the chain.`,
			)
		}
		const latestRelease = legReleases.sort((a, b) => (b.created_at ?? 0) - (a.created_at ?? 0))[0]
		const parsed = parsePathReleaseEvent(latestRelease)
		if (!parsed.ok) {
			throw new Error(`Path release for leg ${legBid.id.slice(0, 8)}… is malformed`)
		}
		const release = parsed.value
		if (release.bidderPubkey !== legBid.pubkey) {
			throw new Error(`Leg ${legBid.id.slice(0, 8)}… release was signed by a different pubkey than the bid`)
		}
		if (!release.cashuToken) {
			throw new Error(`Leg ${legBid.id.slice(0, 8)}… release carries no cashu_token — cannot redeem. Bidder must republish with proofs.`)
		}
		const legChildFromBid = (getTag(legBid, 'child_pubkey') ?? '').toLowerCase()
		const derivedChild = deriveAuctionChildP2pkPubkeyFromXpub(p2pkXpub, release.derivationPath).toLowerCase()
		const releaseChildPubkey = release.childPubkey.toLowerCase()
		if (derivedChild !== legChildFromBid) {
			throw new Error(
				`Leg ${legBid.id.slice(0, 8)}… release derives to ${derivedChild} but the bid was locked to ${legChildFromBid}. Fraudulent bid leg.`,
			)
		}
		if (derivedChild !== releaseChildPubkey) {
			throw new Error(`Leg ${legBid.id.slice(0, 8)}… release child_pubkey tag does not match locally-derived child pubkey`)
		}

		const legCumulative = getBidAmount(legBid)
		const legAmount = legCumulative - runningCumulative
		if (legAmount <= 0) {
			throw new Error(`Leg ${legBid.id.slice(0, 8)}… has non-positive delta (${legAmount}) — chain invariant broken`)
		}
		runningCumulative = legCumulative

		const legMint = getTag(legBid, 'mint') ?? ''
		if (!legMint) throw new Error(`Leg ${legBid.id.slice(0, 8)}… is missing its mint tag`)

		resolvedLegs.push({
			bid: legBid,
			releaseEventId: release.id,
			derivationPath: release.derivationPath,
			bidChildPubkey: legChildFromBid,
			releaseChildPubkey,
			childPubkey: derivedChild,
			mintUrl: legMint,
			cashuToken: release.cashuToken,
			legAmount,
		})
	}

	if (runningCumulative !== winningAmount) {
		throw new Error(
			`Chain sum (${runningCumulative} sats) does not equal winning bid amount (${winningAmount} sats). Chain integrity broken.`,
		)
	}

	// M7 FIX: Bind release token secrets/proof-Ys to bid commitments.
	// The seller's settlement path previously validated derivation paths
	// and child pubkeys but did NOT verify that the cashu token's proofs
	// match the bid's committed lock_secret/proof_y multisets. An attacker
	// could publish a path release with a token containing different proofs
	// (e.g. lower-value proofs) while the bid event committed to different
	// lock_secrets/proof_Ys. The seller would then redeem the wrong proofs.
	//
	// Validate that each leg's token proofs' secrets and proof-Ys match the
	// bid's committed multisets (same approach as validatePathRelease in
	// validation.ts, but applied in the publish path where the seller holds
	// both the bid event and the release token).
	const mintKeysetsByMint = new Map<string, MintKeyset[]>()
	for (const legMintUrl of Array.from(new Set(resolvedLegs.map((leg) => leg.mintUrl)))) {
		mintKeysetsByMint.set(legMintUrl, await nip60Actions.loadAuctionMintKeysets(legMintUrl))
	}

	for (const leg of resolvedLegs) {
		const legBidParsed = parsedBids.find((b) => b.id === leg.bid.id)
		if (!legBidParsed) {
			throw new Error(`M7: Cannot find parsed bid for leg ${leg.bid.id.slice(0, 8)}… to validate token bindings`)
		}
		let decodedToken
		try {
			decodedToken = getDecodedToken(leg.cashuToken, mintKeysetsByMint.get(leg.mintUrl))
		} catch (err) {
			throw new Error(
				`M7: Failed to decode cashu token for leg ${leg.bid.id.slice(0, 8)}…: ${err instanceof Error ? err.message : String(err)}`,
			)
		}
		if (decodedToken.proofs.length !== legBidParsed.lockSecrets.length) {
			throw new Error(
				`M7: Leg ${leg.bid.id.slice(0, 8)}… token has ${decodedToken.proofs.length} proofs but bid committed ${legBidParsed.lockSecrets.length} lock_secrets`,
			)
		}
		const expectedSecrets = new Map<string, number>()
		for (const s of legBidParsed.lockSecrets) expectedSecrets.set(s, (expectedSecrets.get(s) ?? 0) + 1)
		const expectedProofYs = new Map<string, number>()
		for (const y of legBidParsed.proofYs) expectedProofYs.set(y.toLowerCase(), (expectedProofYs.get(y.toLowerCase()) ?? 0) + 1)
		for (let i = 0; i < decodedToken.proofs.length; i++) {
			const proof = decodedToken.proofs[i]
			// Check secret multiset
			const secretCount = expectedSecrets.get(proof.secret) ?? 0
			if (secretCount <= 0) {
				throw new Error(`M7: Leg ${leg.bid.id.slice(0, 8)}… token proof ${i + 1} secret was not committed in the bid's lock_secret tags`)
			}
			if (secretCount === 1) expectedSecrets.delete(proof.secret)
			else expectedSecrets.set(proof.secret, secretCount - 1)
			// Check proof_y multiset
			const proofY = hashToCurveHexFromString(proof.secret).toLowerCase()
			const proofYCount = expectedProofYs.get(proofY) ?? 0
			if (proofYCount <= 0) {
				throw new Error(
					`M7: Leg ${leg.bid.id.slice(0, 8)}… token proof ${i + 1} hash_to_curve(secret) does not match the bid's proof_y set`,
				)
			}
			if (proofYCount === 1) expectedProofYs.delete(proofY)
			else expectedProofYs.set(proofY, proofYCount - 1)
		}
		if (expectedSecrets.size > 0) {
			throw new Error(`M7: Leg ${leg.bid.id.slice(0, 8)}… token is missing one or more secrets committed in the bid`)
		}
		if (expectedProofYs.size > 0) {
			throw new Error(`M7: Leg ${leg.bid.id.slice(0, 8)}… token is missing one or more proof_Ys committed in the bid`)
		}
	}

	preflightAuctionSettlementP2pkChain({
		auctionP2pkXpub: p2pkXpub,
		legs: resolvedLegs.map((leg) => ({
			bidEventId: leg.bid.id,
			mintUrl: leg.mintUrl,
			token: leg.cashuToken,
			derivationPath: leg.derivationPath,
			bidChildPubkey: leg.bidChildPubkey,
			releaseChildPubkey: leg.releaseChildPubkey,
			expectedAmount: leg.legAmount,
			mintKeysets: mintKeysetsByMint.get(leg.mintUrl),
		})),
	})

	// 5b. M6 FIX: Per-leg NUT-7 state check for resumable redemption.
	// The original code did an all-or-nothing pre-check: if ANY proof was
	// spent, it aborted. This made settlement non-resumable — if a
	// previous attempt redeemed leg 1 but crashed before leg 2, retrying
	// would abort because leg 1's proofs are now spent.
	//
	// New approach: check each leg individually.
	//   - ALL proofs spent → leg was already redeemed by this seller in a
	//     previous attempt → skip (resumable).
	//   - SOME proofs spent (not all) → early-exit fraud or partial spend
	//     → abort.
	//   - ALL proofs unspent → redeem normally.
	//
	// This makes settlement resumable: the seller can retry and already-
	// spent legs are detected and skipped automatically.
	const legProofStates = new Map<string, Map<string, Nut7ProofState>>()
	for (const leg of resolvedLegs) {
		const legBid = parsedBids.find((b) => b.id === leg.bid.id)
		if (!legBid) continue
		const states = await checkProofStateBatch(leg.mintUrl, legBid.proofYs)
		legProofStates.set(leg.bid.id, states)
	}

	// Classify each leg as 'already-redeemed', 'fraud', or 'pending'.
	const alreadyRedeemedLegs = new Set<string>()
	for (const leg of resolvedLegs) {
		const legBid = parsedBids.find((b) => b.id === leg.bid.id)
		if (!legBid) continue
		const states = legProofStates.get(leg.bid.id)
		if (!states) continue
		let spentCount = 0
		let totalProofs = legBid.proofYs.length
		for (const proofY of legBid.proofYs) {
			if (states.get(proofY.toLowerCase()) === 'spent') spentCount++
		}
		if (spentCount === totalProofs) {
			// All proofs spent — but bare SPENT proves consumption, not WHO
			// consumed them. Pre-locktime only the seller's lock key (child
			// privkey from the auction xpriv + this leg's path) can spend, so
			// all-spent pre-locktime IS seller-bound redemption evidence
			// ('already redeemed', e.g. a previous settlement attempt that
			// crashed after this leg). Post-locktime the bidder's refund path
			// is also open — then all-spent stays ambiguous and the leg must
			// be redeemed (the mint answers 'already spent' only if the
			// bidder refunded; see recovery in the receive loop below).
			if (now < legBid.locktime) {
				alreadyRedeemedLegs.add(leg.bid.id)
			}
		} else if (spentCount > 0) {
			// Some but not all proofs spent — early-exit fraud or partial
			// spend. Abort the entire settlement.
			throw new Error(
				`NUT-7 check failed: leg ${leg.bid.id.slice(0, 8)}… has ${spentCount}/${totalProofs} proofs spent (partial spend detected). ` +
					`This may indicate early-exit fraud by the bidder. Aborting settlement.`,
			)
		}
		// spentCount === 0 → all unspent, will redeem below
	}

	// 6. For each leg in the chain (oldest → newest): derive the
	// seller's child privkey from the auction xpriv + leg's path, then
	// receive the locked Cashu token. Order doesn't matter
	// cryptographically but processing oldest-first matches the order
	// the bidder locked them.
	// M6 FIX: Skip legs that were already redeemed (detected in step 5b).
	// This makes settlement resumable — a crashed attempt can be retried
	// and already-spent legs are automatically skipped.
	const payouts: Array<{ bidEventId: string; amount: number; status: string }> = []
	for (const leg of resolvedLegs) {
		if (alreadyRedeemedLegs.has(leg.bid.id)) {
			// Leg was already redeemed in a previous attempt — include in
			// payouts so the settlement event records the full chain.
			payouts.push({ bidEventId: leg.bid.id, amount: leg.legAmount, status: 'redeemed' })
			continue
		}
		const childPrivkey = await nip60Actions.getAuctionHdChildPrivkey({
			derivationPath: leg.derivationPath,
			expectedPubkey: leg.childPubkey,
		})
		let redeemed = false
		try {
			// Pass leg.mintUrl explicitly so receiveLockedEcash skips the
			// `getDecodedToken(token)` step that fails on v2 short keyset IDs.
			redeemed = await nip60Actions.receiveLockedEcash(leg.cashuToken, childPrivkey, leg.mintUrl)
		} catch (err) {
			throw tagBidError(`settlement-receive-leg-${leg.bid.id.slice(0, 8)}`, err)
		}
		if (!redeemed) {
			throw new Error(`Cashu redemption did not complete for leg ${leg.bid.id.slice(0, 8)}…`)
		}
		payouts.push({ bidEventId: leg.bid.id, amount: leg.legAmount, status: 'redeemed' })
	}

	// 7. Publish kind-1024. `path_release` references the LATEST leg's
	// release (the one the bidder's "settle" button surfaced); the
	// chain history is reconstructible from the chain of bid events
	// via their prev_bid tags. `payouts` enumerates each leg the
	// seller actually redeemed.
	const latestLeg = resolvedLegs[resolvedLegs.length - 1]
	const template: EventTemplate = {
		kind: kind1024,
		content: '',
		tags: (await import('@/lib/auction/tagBuilders')).buildSettlementTags({
			auctionRootEventId,
			auctionCoordinate,
			status: 'settled',
			closeAt,
			finalAmount: winningAmount,
			winningBidId,
			winnerPubkey,
			pathReleaseEventId: latestLeg.releaseEventId,
			payouts,
		}),
		created_at: Math.floor(Date.now() / 1000),
	}
	const event = await signNostrEvent(template)
	await publishRequired(event)
	return event.id
}

export const usePublishAuctionSettlementMutation = () => {
	const queryClient = useQueryClient()

	// ADR-0011 review R3: capture the mutate fn so the error toast's
	// "Publish anyway" action can re-run the SAME settlement with the seller
	// override (`dleqEvidenceOverride`), which records the unresolved DLEQ
	// evidence on the terminal event instead of leaving the seller stuck.
	const overrideRef = useRef<((variables: AuctionSettlementFormData) => void) | null>(null)

	const mutation = useMutation({
		mutationFn: async (formData: AuctionSettlementFormData) => publishAuctionSettlement(formData),
		onSuccess: async (_eventId, variables) => {
			await queryClient.invalidateQueries({ queryKey: auctionKeys.details(variables.auctionEventId) })
			await queryClient.invalidateQueries({ queryKey: auctionKeys.bids(variables.auctionEventId) })
			await queryClient.invalidateQueries({ queryKey: auctionKeys.settlements(variables.auctionEventId) })
			await queryClient.invalidateQueries({ queryKey: auctionKeys.all })
			toast.success('Auction settlement published')
		},
		onError: (error, variables) => {
			console.error('Failed to publish auction settlement:', error)
			const message = error instanceof Error ? error.message : String(error)
			if (message.includes('DLEQ evidence is unavailable')) {
				toast.error(`Failed to publish settlement: ${message}`, {
					action: {
						label: 'Publish anyway',
						onClick: () => overrideRef.current?.({ ...variables, dleqEvidenceOverride: true }),
					},
				})
				return
			}
			toast.error(`Failed to publish settlement: ${message}`)
		},
	})
	overrideRef.current = mutation.mutate
	return mutation
}

// ---------------------------------------------------------------------------
// Auction Claim Order — winner submits shipping address after settlement
// ---------------------------------------------------------------------------

export interface AuctionClaimFormData {
	auctionEventId: string
	auctionCoordinates: string
	settlementEventId: string
	sellerPubkey: string
	finalAmount: number
	shippingAddress: {
		name: string
		firstLineOfAddress: string
		city: string
		zipPostcode: string
		country: string
		additionalInformation?: string
	}
	email?: string
	phone?: string
	notes?: string
}

/**
 * Creates a Kind 16 order event that references the won auction.
 * This is identical to a normal order creation but uses an `a` tag
 * pointing at the auction coordinate and an `e` tag pointing at the
 * settlement event instead of product `item` tags.
 */
export const publishAuctionClaimOrder = async (formData: AuctionClaimFormData): Promise<string> => {
	const buyerPubkey = await resolveAuctionClaimBuyerPubkey()
	const orderId = uuidv4()
	const claimFields = {
		orderId,
		auctionCoordinates: formData.auctionCoordinates,
		auctionEventId: formData.auctionEventId,
		settlementEventId: formData.settlementEventId,
		buyerPubkey,
		sellerPubkey: formData.sellerPubkey,
		totalAmountSats: formData.finalAmount,
		shippingAddress: formData.shippingAddress,
		email: formData.email,
		phone: formData.phone,
		notes: formData.notes,
	}

	// NIP-59 private claim — #1252-gated raw-signer path (see
	// `privateAuctionClaimMessage.ts`). The public marker + gift-wrap publish
	// stay on the library-agnostic I/O seam.
	const privateClaim = await createPrivateAuctionClaimMessageForActiveSigner(claimFields)
	const privateRelayUrls = await publishRequiredPrivateGiftWrap(privateClaim.giftWrap)

	const template: EventTemplate = {
		kind: ORDER_PROCESS_KIND,
		content: '',
		tags: buildAuctionClaimPublicMarkerTags(claimFields),
		created_at: Math.floor(Date.now() / 1000),
	}

	const event = await signNostrEvent(template)
	await publishAuctionClaimMarkerToPrivateRelays(event, privateRelayUrls)

	return event.id
}

/**
 * Publish through the seam and fail closed on zero relay ACKs.
 *
 * `io.ts` documents an empty `publishedRelays` as "no relay accepted it, which
 * callers must treat as a publish failure". That is enforced for free by the
 * NDK bridge (it throws `NDKPublishError` below `requiredRelayCount = 1`), but
 * the applesauce adapter RESOLVES with the ACK-filtered set
 * (`io-applesauce.ts`), so an unchecked call reports a zero-ACK publish as a
 * success. This wrapper makes the contract adapter-independent (review
 * 2026-09-18, item 4). `publishRequiredPrivateGiftWrap` /
 * `publishAuctionClaimMarkerToPrivateRelays` keep their own checks because they
 * need the accepted relay URLs.
 */
async function publishRequired(event: NostrEvent, options?: PublishOptions): Promise<void> {
	const result = await publishNostrEvent(event, options)
	if (result.publishedRelays.size === 0) {
		throw new Error(`No relay accepted the event (kind ${event.kind}, id ${event.id}) — treating as a publish failure`)
	}
}

async function publishRequiredPrivateGiftWrap(event: NostrEvent): Promise<string[]> {
	const result = await publishNostrEvent(event)
	if (result.publishedRelays.size === 0) {
		throw new Error('Encrypted auction claim details could not be published')
	}
	return Array.from(result.publishedRelays)
}

async function publishAuctionClaimMarkerToPrivateRelays(event: NostrEvent, privateRelayUrls: string[]): Promise<void> {
	const result = await publishNostrEvent(event, { relayUrls: privateRelayUrls })
	if (result.publishedRelays.size === 0) {
		throw new Error('Auction claim marker could not be published to the private claim relays')
	}
}

/**
 * Resolve the buyer identity written into the public claim marker.
 *
 * The base implementation compared `ndk.activeUser?.pubkey` against the active
 * signer's pubkey and threw "Active user does not match active signer."; the
 * seam exposes identity only through `getUser()`, which is itself
 * signer-derived (`@/lib/stores/ndk` `getUser()` → the active signer's user).
 * The two values therefore cannot diverge through the seam today, so the
 * comparison is vacuous here rather than removed. The identity property that
 * actually matters — the pubkey written into the public marker is the identity
 * that encrypted the private claim — holds because
 * `privateAuctionClaimMessage.ts` produces the gift wrap from the same active
 * signer. The #1252 signer migration re-points `getUser()`; if it ever makes
 * identity independent of the signer, that migration MUST re-establish the
 * cross-account assertion (review 2026-09-18, item 5).
 */
async function resolveAuctionClaimBuyerPubkey(): Promise<string> {
	const user = await getUser()
	const signerPubkey = user?.pubkey ?? ''
	if (!HEX_PUBKEY_RE.test(signerPubkey)) throw new Error('Active signer pubkey is not a 32-byte hex Nostr pubkey.')

	return signerPubkey
}

export const usePublishAuctionClaimOrderMutation = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: publishAuctionClaimOrder,
		onSuccess: async (_orderId, variables) => {
			await queryClient.invalidateQueries({ queryKey: auctionKeys.settlements(variables.auctionEventId) })
			await queryClient.invalidateQueries({ queryKey: auctionKeys.details(variables.auctionEventId) })
			await queryClient.invalidateQueries({ queryKey: orderKeys.all })
			const user = await getUser()
			if (user?.pubkey) {
				await queryClient.invalidateQueries({ queryKey: orderKeys.byBuyer(user.pubkey) })
				await queryClient.invalidateQueries({ queryKey: orderKeys.byPubkey(user.pubkey) })
			}
			toast.success('Shipping details submitted — the seller has been notified')
		},
		onError: (error) => {
			console.error('Failed to submit auction claim:', error)
			toast.error(`Failed to submit claim: ${error instanceof Error ? error.message : String(error)}`)
		},
	})
}
